import React, { useEffect, useLayoutEffect, useCallback, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { fetchFeed, fetchMoreFeed, fetchTrendingFeed } from '../../redux/thunk/feedThunk';
import { refreshTier } from '../../redux/thunk/tierThunk';
import { clearFeed, setTimeMode, setSubTab as setFeedSubTab, setTrendingItems, setTasteNotice } from '../../redux/slices/feedSlice';
import { setAutoPlay } from '../../redux/slices/musicPlayerSlice';
import { addToNextUp } from '../../utils/queueAdd';
import { setListContext } from '../../redux/slices/listContextSlice';
import { resolveTrackSrc } from '../../utils/resolvePlaybackSrc';
import { useToast } from '../../contexts/ToastContext';
import { ReactComponent as SurpriseIcon } from '../../assets/icons/surprise.svg';
import { setActiveSearch, setTrackQuery, setArtistQuery, setMixQuery } from '../../redux/slices/djpalSlice';
import { searchTrack, searchArtist, searchMixes } from '../../redux/thunk/djpalThunk';
import useDjpalConnect from '../../hooks/useDjpalConnect';
import { setActiveTab } from '../../redux/slices/appSlice';
import { setPendingStart } from '../../redux/slices/whatsNextSlice';
import { fetchLikes } from '../../redux/thunk/likesThunk';
import { fetchFollowedArtists } from '../../redux/thunk/followedArtistsThunk';
import DJPalAudioPreview from '../DJPal/DJPalAudioPreview';
import { delayedClick, useMixPlayback, isSupportedMixUrl, mixTrackingKey } from '../DJPal/MixPlayer';
import LikeButton from '../LikeButton';
import MixLikeButton from '../MixLikeButton';
import TrackContextMenu from '../TrackContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import AddToPlaylistModal from '../AddToPlaylistModal';
import FeedWaveform, { resetWaveformQueue } from '../FeedWaveform';
import FakeTrackWaveform, { barsPath } from '../FakeTrackWaveform';
import MixFeedWaveform from '../MixFeedWaveform';
import MultiArtistText from '../MultiArtistText';
import EntityHoverCard from '../EntityHoverCard';
import { openMixpediaLabel } from '../../redux/thunk/appNavThunk';
import { useTierGate } from '../../hooks/useTierGate';
import { GoatBadge } from '../Tier';
import { isWeb, isNative, isMobileEnv, isSamplesOnly, notifyWebOnly } from '../../utils/webOnly';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { useFeedWaveformMode } from '../../utils/feedWaveform';
import { resolveCover } from '../../utils/cover';
import { entityId, publicEntityId } from '../../utils/entityId';
import { track as trackEvent } from '../../utils/analytics';
import CoverArt from '../CoverArt';
import { feedEraLabel } from '../../utils/feedEraLabel';
import { useDesignV2 } from '../../utils/designV2';
import DiscoverHero, { HeroListenPlayButton } from '../v2/DiscoverHero';
import { SectionHeader } from '../v2/PageTabs';
import '../DJPal/DJPal.scss';
import './FeedTab.scss';

// Persist scroll position and visible count across tab switches (remount)
// and sub-tab switches (tracks ↔ mixes within the same mount).
// Per-sub-tab so each list remembers its own position.
// Sub-tab lives in Redux (feedSlice.subTab) so it participates in app-level nav history.
const _savedScrollTop = { tracks: 0, mixes: 0 };
const _savedVisibleCount = { tracks: 6, mixes: 6 };
let _hasFetchedOnce = false; // survives tab switches (unlike useRef which resets on remount)

// Seed-badge hover analytics, deduped per session per source so mouseovers
// while scanning the feed don't spam PostHog.
const _seedBadgeHoverSent = new Set();
function trackSeedBadgeHover(source) {
    if (_seedBadgeHoverSent.has(source)) return;
    _seedBadgeHoverSent.add(source);
    trackEvent('feed_seed_badge_hovered', { source });
}

function formatFeedDate(d) {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// DESIGN V2 — human dates: "3 weeks ago" inside ~5 weeks, then "Jun 7"
// (this year) or "Jun 7, 2025". The old design keeps the machine format.
function formatFeedDateRelative(d) {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    const diffMs = Date.now() - date.getTime();
    const day = 86400000;
    if (diffMs >= 0) {
        if (diffMs < 3600000) return 'just now';
        if (diffMs < day) { const h = Math.floor(diffMs / 3600000); return `${h}h ago`; }
        if (diffMs < 7 * day) { const n = Math.floor(diffMs / day); return n === 1 ? 'yesterday' : `${n} days ago`; }
        if (diffMs < 35 * day) { const n = Math.floor(diffMs / (7 * day)); return n === 1 ? '1 week ago' : `${n} weeks ago`; }
    }
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' });
}
const feedDateLabel = (d, v2) => (v2 ? formatFeedDateRelative(d) : formatFeedDate(d));

// Seconds → "2h 01m" / "48m" for the v2 hero meta. Null when unknown.
function formatHeroDuration(sec) {
    const n = Number(sec);
    if (!n || n <= 0) return null;
    const h = Math.floor(n / 3600), m = Math.round((n % 3600) / 60);
    return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

// Mix.link → YouTube id for the playlist item (mirrors MixContextMenu's
// ytIdFromLink — a mix enters a playlist as a track-shaped row playing
// through the same YouTube/SoundCloud resolution as any linked track).
function ytIdFromMixLink(link) {
    const m = /(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/.exec(link || '');
    return m ? m[1] : null;
}

// Mobile-web detection at module scope — cheap, stable for the lifetime
// of the tab. Desktop/Electron keep the existing per-row play button;
// mobile web gets whole-row-click-to-play (Spotify/SoundCloud pattern).
// Mobile environment = native (iOS/Android) OR a phone-sized web viewport in
// EITHER orientation. Orientation-independent so a landscape phone keeps the
// mobile UI instead of flipping to the desktop layout.
const isMobileWebEnv = isMobileEnv();

// What .feed-card-artwork actually renders: 120px CSS on desktop, 56px on
// the mobile breakpoint (FeedTab.scss). Must stay in lockstep with
// feedThunk.js's warmFeedCovers or the warm fills the cache with a variant
// no card ever requests.
const FEED_ART_PX = isMobileWebEnv ? 56 : 120;

// Mobile-only actions menu: collapses Copy / Add-to-playlist / Add-to-Next-Up
// / Start-set into a kebab so the "played by …" reason text has room to
// breathe. Also exposes "Open in Mixpedia" — the only way to reach the
// track/mix page on mobile, since the card body taps play instead of
// linking.

// On mobile the card body is tap-to-play, so the tap that DISMISSES a
// popover/menu (we close on touchstart/mousedown) must not also land as a
// click on whatever card happens to be underneath — that read as "I closed
// the ⋯ menu and it play/paused the track". Native menus swallow the
// dismissing tap; do the same: arm a one-shot capture-phase click blocker
// for the click that completes this press. Disarmed after it fires or after
// 1s (a press that long isn't a tap and produces no click anyway).
function swallowNextClickOnMobile() {
    if (!isMobileWebEnv) return;
    const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); disarm(); };
    const timer = setTimeout(() => disarm(), 1000);
    function disarm() {
        clearTimeout(timer);
        document.removeEventListener('click', swallow, true);
    }
    document.addEventListener('click', swallow, true);
}

// window.innerHeight lies about where a fixed-position popover can end: the
// jinke player panel (desktop bar / mobile floating pill) and the mobile
// bottom nav are position:fixed OVER the viewport bottom, so "fits below per
// innerHeight" can still mean "opens underneath the player". Returns the top
// edge of the highest visible bottom overlay — the real bottom limit for
// fixed positioning. A hidden / not-yet-laid-out overlay measures 0×0 at the
// viewport origin, and no real bottom bar covers most of the screen — treat
// such rects as bogus (same defense as WhatsNextTab::getBottomOcclusion).
function usableViewportBottom() {
    let bottom = window.innerHeight;
    ['.music-player-panel', '.mobile-bottom-nav'].forEach((sel) => {
        const el = document.querySelector(sel);
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.height <= 0 || r.top <= window.innerHeight * 0.5) return;
        if (r.top < bottom) bottom = r.top;
    });
    return bottom;
}

const FeedActionsKebab = ({ items }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const menuRef = useRef(null);
    // Portal'd + fixed-positioned (mirrors TrackContextMenu) so neither the
    // feed scroller nor the card can clip the menu. Opens DOWN by default and
    // only flips up when it would clip off the bottom of the viewport — the
    // old always-up absolute positioning pushed the menu off-screen on cards
    // near the top of the feed.
    const [menuStyle, setMenuStyle] = useState(null);
    useLayoutEffect(() => {
        if (!open || !wrapRef.current) {
            if (!open) setMenuStyle(null);
            return;
        }
        const rect = wrapRef.current.getBoundingClientRect();
        const menuHeight = 10 + items.length * 38; // padding + per-item height
        const menuWidth = 210; // min-width 200 + padding
        const openUp = usableViewportBottom() - rect.bottom < menuHeight;
        // Align to the button's left edge (menu extends right) unless that
        // would clip off the right side of the viewport — then right-align.
        const alignLeft = rect.left + menuWidth <= window.innerWidth - 8;
        setMenuStyle({
            position: 'fixed',
            ...(alignLeft
                ? { left: rect.left, right: 'auto' }
                : { right: window.innerWidth - rect.right, left: 'auto' }),
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
                : { top: rect.bottom + 4, bottom: 'auto' }),
            zIndex: 10001,
        });
    }, [open, items.length]);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            // The menu is portal'd, so it is NOT inside wrapRef — it must be
            // checked separately or mousedown/touchstart closes the menu
            // before the item's click ever fires.
            if (wrapRef.current && !wrapRef.current.contains(e.target) &&
                (!menuRef.current || !menuRef.current.contains(e.target))) {
                setOpen(false);
                swallowNextClickOnMobile();
            }
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('touchstart', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('touchstart', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    return (
        <span className="feed-kebab-wrap" ref={wrapRef}>
            <button
                type="button"
                className="feed-action-btn feed-kebab-btn"
                title="More"
                aria-label="More actions"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(v => !v); }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2"/>
                    <circle cx="12" cy="12" r="2"/>
                    <circle cx="19" cy="12" r="2"/>
                </svg>
            </button>
            {open && ReactDOM.createPortal(
                <div
                    className="feed-kebab-menu"
                    ref={menuRef}
                    style={menuStyle || { position: 'fixed', visibility: 'hidden', zIndex: 10001 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {items.map((it, i) => (
                        <button
                            key={i}
                            type="button"
                            className="feed-kebab-menu-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setOpen(false);
                                it.onClick(e);
                            }}
                        >
                            <span className="feed-kebab-menu-icon">{it.icon}</span>
                            <span>{it.label}</span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </span>
    );
};

const KebabIcon = {
    mixpedia: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
    ),
    copy: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
    ),
    playlist: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="11" x2="21" y2="11"/><line x1="3" y1="17" x2="12" y2="17"/><line x1="18" y1="14" x2="18" y2="22"/><line x1="14" y1="18" x2="22" y2="18"/></svg>
    ),
    queue: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
    ),
    set: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2.5"/><path d="M7.5 12 H20"/><path d="M16 8 l4 4 -4 4"/></svg>
    ),
};

const FeedCard = React.memo(({ item, index, timeMode, onCopy, onSearchTrack, onSearchArtist, onSearchMix, onAddToQueue, onAddToPlaylist, onStartSet, copiedId, playContext, surpriseMode = false }) => {
    const dispatch = useDispatch();
    const track = item.track;
    const art = track.artwork || track.artworkUrl || track.image;
    // Surprise-mode items lead the reason line with the track's style
    // (e.g. "Techno, Played in 3 mixes") so an unfamiliar pick still tells you
    // what it is. Only in Surprise mode — regular discovery-fill items (also
    // isRandom) don't get it. Style is more specific than genre for DJ tracks,
    // so prefer styles[0], fall back to genres[0]. Handles both array and
    // comma-string shapes coming off the API.
    const styleLabel = (() => {
        if (!surpriseMode || !item.isRandom) return null;
        const pick = (v) => {
            if (Array.isArray(v)) return v.find((s) => s && String(s).trim()) || null;
            if (typeof v === 'string' && v.trim()) return v.split(',')[0].trim();
            return null;
        };
        return pick(track.styles) || pick(track.genres) || null;
    })();
    const previewSrc = track.previewUrl || track.sampleUrl || track.preview_url || track.preview;
    const previewId = `feed-${index}`;
    // Server flag FEED_WAVEFORM_MODE: FULL (eager real) | SAMPLE (eager real,
    // sample peaks only) | FAKE (synthetic up front, swap to real on play).
    const waveformMode = useFeedWaveformMode();

    // Wrapped in a span so the mobile media query can hide it — the date
    // cramps the reason text against the kebab on narrow screens.
    const v2 = useDesignV2();
    const mixDateLabel = item.mixDate
        ? <span className="feed-reason-date">{` · ${feedDateLabel(item.mixDate, v2)}`}</span>
        : null;

    // Popover expansion: click on "X mixes" opens a small dropdown anchored
    // to the link with a list of mixes (thumbnails + clickable titles). No
    // layout shift — feed below stays still. Auto-closes on outside click,
    // Escape, or scroll. Portal'd to body so card overflow doesn't clip it.
    const djpalToken = useSelector((state) => state.djpal.token);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 280 });
    // Mobile-only: the now-playing overlay on the cover art (the inline round
    // play button is hidden on mobile). Driven purely off the player's
    // `currentTrack` — the same value the Now Playing page uses, which already
    // follows auto-advance — matched by title + artist. CSS only; touches no
    // playback. Stays on the track while paused (currentTrack doesn't clear).
    const playerCurrent = useSelector((state) => state.musicPlayer.currentTrack);
    const playerIsPlaying = useSelector((state) => state.musicPlayer.isPlaying);
    const coverPlaying = isMobileWebEnv && !!playerCurrent
        && (playerCurrent.name || '') === (track.title || '')
        && (playerCurrent.singer || '') === (track.artist || '');
    // Paused: this is still the loaded track, but the bars freeze flat so it
    // doesn't read as actively playing (SoundCloud-style paused waveform).
    const coverPaused = coverPlaying && !playerIsPlaying;
    const [inlineMixes, setInlineMixes] = useState(null);
    const [inlineLoading, setInlineLoading] = useState(false);
    const [inlineLoadingMore, setInlineLoadingMore] = useState(false);
    const [allMixesLoaded, setAllMixesLoaded] = useState(false);
    const popoverRef = useRef(null);

    // The trigger label shows the track's full mix count (e.g. "228 mixes"), so
    // the popover must be able to reach all of them — not a hard cap of 10. Fetch
    // a page at a time and load the next page as the user scrolls the (now
    // internally-scrollable) popover. labelMixCount bounds the total so we stop
    // once we've surfaced as many rows as the label promised.
    const MIX_PAGE = 15;
    const labelMixCount = item.playedCount || item.mixRefcount || 5;
    const fetchMixesPage = useCallback(async (page) => {
        const isFirst = page === 1;
        if (isFirst) setInlineLoading(true); else setInlineLoadingMore(true);
        try {
            const trackName = track.artist && track.title ? `${track.artist} - ${track.title}` : (track.title || '');
            // Pass _trackName alongside trackId so the server falls back to a
            // name search when the id yields nothing (stale mix_refcount, or the
            // track's mixes have no public_id) — otherwise a "20 mixes" label can
            // open to "No mixes found". trackId stays the first key (the primary
            // filter); _trackName is only read for the fallback.
            const catalogTrackId = entityId(track);
            const filters = catalogTrackId ? { trackId: catalogTrackId, _trackName: trackName } : { trackIncluded: trackName };
            // For trending items the count ("Played recently in N mixes") is the
            // windowed play count (last `windowDays`, default 90). Constrain the
            // popover list to that same window so it shows those N mixes — not the
            // track's all-time mix history, which would over-list (e.g. "4 mixes"
            // opening to 7). Other item types (artist_track_companion) label with
            // the all-time mixRefcount and must stay un-windowed.
            const dateRange = (item.type === 'trending' && item.windowFrom && item.windowTo)
                ? { from: item.windowFrom, to: item.windowTo }
                : null;
            // Lite search: the popover renders only mix meta (title/DJ/date/
            // cover), so skip the tracklist joins server-side — the full
            // search ran one sequential tracklist query PER MIX and made the
            // popover take seconds to open.
            const searchFn = window.electron.djpal.searchMixesLite || window.electron.djpal.searchMixes;
            const result = await searchFn(djpalToken, filters, dateRange, { page, limit: MIX_PAGE });
            const data = result?.data || result || {};
            const arr = data.results || data.result || (Array.isArray(data) ? data : []);
            const batch = Array.isArray(arr) ? arr : [];
            let total = 0;
            setInlineMixes(prev => {
                const next = isFirst ? batch : [...(prev || []), ...batch];
                total = next.length;
                return next;
            });
            // Stop when the server returns a short page or we've reached the label.
            if (batch.length < MIX_PAGE || total >= labelMixCount) setAllMixesLoaded(true);
        } catch (err) {
            console.error('[FeedCard] mix popover fetch failed', err);
            if (isFirst) setInlineMixes([]);
            setAllMixesLoaded(true);
        } finally {
            if (isFirst) setInlineLoading(false); else setInlineLoadingMore(false);
        }
    }, [djpalToken, track, labelMixCount, item.type, item.windowFrom, item.windowTo]);
    const loadMoreMixes = useCallback(() => {
        if (inlineLoading || inlineLoadingMore || allMixesLoaded) return;
        const nextPage = Math.floor((inlineMixes?.length || 0) / MIX_PAGE) + 1;
        fetchMixesPage(nextPage);
    }, [inlineLoading, inlineLoadingMore, allMixesLoaded, inlineMixes, fetchMixesPage]);
    const onPopoverScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMoreMixes();
    }, [loadMoreMixes]);

    const toggleInlineMixes = useCallback((e) => {
        e.stopPropagation();
        if (popoverOpen) { setPopoverOpen(false); return; }
        // Position below the trigger, right-aligned (so it doesn't overflow
        // the right edge for a link near the right side of the viewport).
        const rect = e.currentTarget.getBoundingClientRect();
        const width = 320;
        const margin = 8;
        let left = rect.right - width;
        if (left < margin) left = margin;
        const maxLeft = window.innerWidth - width - margin;
        if (left > maxLeft) left = maxLeft;
        // Space is measured to the REAL usable bottom (player pill + mobile
        // nav are fixed overlays), and against the popover's true cap — keep
        // 280 in sync with .feed-mixes-popover max-height. Flip up only when
        // below can't fit the full popover AND above is roomier; either way
        // cap the height to the chosen side so the list scrolls instead of
        // overflowing. When flipped, anchor by `bottom` so content shorter
        // than the cap still hugs the trigger.
        const POPOVER_MAX_HEIGHT = 280;
        const spaceBelow = usableViewportBottom() - rect.bottom - 6 - margin;
        const spaceAbove = rect.top - 6 - margin;
        // The row count is known before the fetch (it's the chip's own "N
        // mixes" label), so estimate the real height (54px/row + container
        // padding, capped by CSS) instead of always demanding the full cap —
        // otherwise a 4-row popover flips up despite fitting below.
        const estimatedHeight = Math.min(POPOVER_MAX_HEIGHT, 12 + labelMixCount * 54);
        const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
        const maxH = Math.max(120, Math.min(POPOVER_MAX_HEIGHT, Math.floor(openUp ? spaceAbove : spaceBelow)));
        setPopoverPos(openUp
            ? { bottom: window.innerHeight - rect.top + 6, left, width, maxH }
            : { top: rect.bottom + 6, left, width, maxH });
        setPopoverOpen(true);
        if (inlineMixes === null) fetchMixesPage(1);
    }, [popoverOpen, inlineMixes, fetchMixesPage, labelMixCount]);

    // Close popover on outside click, Escape, or scroll.
    useEffect(() => {
        if (!popoverOpen) return;
        const onDocClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setPopoverOpen(false);
                swallowNextClickOnMobile();
            }
        };
        const onKey = (e) => { if (e.key === 'Escape') setPopoverOpen(false); };
        // Close on PAGE scroll (the fixed popover would drift out of place), but
        // NOT when the scroll happens INSIDE the popover's own list — otherwise
        // the first wheel tick closes it and the page scrolls instead.
        const onScroll = (e) => {
            if (popoverRef.current && popoverRef.current.contains(e.target)) return;
            setPopoverOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [popoverOpen]);

    // The DJ credit ("Played by …") can be several co-DJs. The backend now sends
    // `mixDjs` ([{id, name}]) resolved from the mix's artist_ids, so render each
    // as its own clickable segment (id-based nav) — falling back to the flat
    // `mixDj` string (comma-split, name-based) only when no ids resolved. Same
    // pattern as the track artist line below, so co-DJs never merge into one
    // bogus artist link.
    // Hover preview wrapper for artist name segments — small card with the
    // artist's photo + Follow, so following a DJ doesn't require leaving the
    // feed. Desktop-only (EntityHoverCard no-ops without a hover pointer).
    const artistHoverWrap = useCallback((seg, node) => (
        <EntityHoverCard
            type="artist"
            entity={seg}
            onNavigate={() => onSearchArtist({ stopPropagation: () => {} }, seg.name, seg.id, seg.artworkUrl)}
        >
            {node}
        </EntityHoverCard>
    ), [onSearchArtist]);

    const renderDjCredit = (fallbackText) => (
        <MultiArtistText
            artists={item.mixDjs}
            fallbackText={fallbackText}
            onSelect={(a) => onSearchArtist({ stopPropagation: () => {} }, a.name, a.id, a.artworkUrl)}
            segmentClassName="feed-inline-link"
            segmentWrapper={artistHoverWrap}
        />
    );

    // Same idea for the "in <Mix>" links: hover shows the mix cover, DJ, date
    // and a Like pill. All four reason-line variants share this one node.
    const renderMixLink = () => (
        <EntityHoverCard
            type="mix"
            entity={{ title: item.mixTitle, publicId: item.mixPublicId, link: item.mixLink, djName: item.mixDj, date: item.mixDate }}
            onNavigate={() => onSearchMix({ stopPropagation: () => {} }, item)}
        >
            <span className="feed-inline-link" onClick={(e) => { e.stopPropagation(); onSearchMix(e, item); }}>{item.mixTitle}</span>
        </EntityHoverCard>
    );

    // ①/② cards: tiny icon leading the reason line answering "why am I
    // seeing this?" — the seed artist is followed (person), or seeded from
    // the user's liked tracks (heart) or synced DJ library (crate). Tooltip
    // carries the detail. The followed shape is `{followed: true}` with NO
    // `source` key (server keeps it invisible to pre-badge clients).
    const seedBadge = (() => {
        const ss = item.seedSource;
        // ⑤ "labels you follow" (Erez 11-01): a record glyph, the label named in the tooltip
        if (ss?.followedLabel) {
            const labelName = ss.labelName || track.label || 'a label';
            return (
                <span className="feed-seed-badge" title={`From ${labelName}, a label you follow`} onMouseEnter={() => trackSeedBadgeHover('label')}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2.5"/></svg>
                </span>
            );
        }
        if (!ss || (!ss.source && !ss.followed)) return null;
        // The seed is ONE artist. `artistName` is stamped server-side
        // (attachArtistsTo) because the public-API sanitizer rewrites nested
        // artist ids to public_ids — the id lookups below only match if that
        // ever changes. NEVER fall back to mixDj/sourceArtist: those are
        // multi-artist concat strings ("Roddy Lima, Sarah de Warren") that
        // claim the user liked ALL of them. No single name → no badge.
        const seedName = ss.artistName
            || (item.mixDjs || []).find((a) => a && a.id === ss.artistId)?.name
            || (track.artists || []).find((a) => a && a.id === ss.artistId)?.name;
        if (!seedName) return null;
        if (ss.followed) {
            return (
                <span className="feed-seed-badge" title={`You follow ${seedName}`} onMouseEnter={() => trackSeedBadgeHover('followed')}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
                </span>
            );
        }
        const n = ss.count || 0;
        const tracksText = n === 1 ? '1 track' : `${n} tracks`;
        const tip = ss.source === 'library'
            ? `${seedName}: ${tracksText} in your library`
            : `You've liked ${tracksText} by ${seedName}`;
        return (
            <span className="feed-seed-badge" title={tip} onMouseEnter={() => trackSeedBadgeHover(ss.source)}>
                {ss.source === 'library'
                    ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7l2-4h14l2 4"/><rect x="3" y="7" width="18" height="13" rx="2"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                    : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>}
            </span>
        );
    })();

    const reasonText = (() => {
        if (item.type === 'artist_played' && item.sourceArtist) {
            const djName = item.mixDj || item.sourceArtist;
            return (
                <>
                    {seedBadge}
                    Played by{' '}
                    {renderDjCredit(djName)}
                    {item.mixTitle && (
                        <> in{' '}{renderMixLink()}</>
                    )}
                    {mixDateLabel}
                </>
            );
        }
        if (item.type === 'artist_track_companion') {
            const refcount = item.mixRefcount || 0;
            const fl = item.seedSource?.followedLabel ? item.seedSource : null;
            // a followed-label pick leads with the label (a link with its hover card) instead of the style
            const stylePrefix = fl && fl.labelId
                ? <>On{' '}<EntityHoverCard type="label" entity={{ publicId: fl.labelId, name: fl.labelName || track.label }}
                        onNavigate={() => dispatch(openMixpediaLabel({ publicId: fl.labelId, name: fl.labelName || track.label, from: 'feed' }))}>
                        <span className="feed-inline-link" onClick={(e) => { e.stopPropagation(); dispatch(openMixpediaLabel({ publicId: fl.labelId, name: fl.labelName || track.label, from: 'feed' })); }}>{fl.labelName || track.label}</span>
                    </EntityHoverCard>, </>
                : styleLabel ? <><span className="feed-reason-style">{styleLabel}</span>, </> : null;
            if (refcount <= 1 && item.mixTitle) {
                return (
                    <>
                        {seedBadge}
                        {stylePrefix}Played in{' '}
                        {renderMixLink()}
                        {mixDateLabel}
                    </>
                );
            }
            if (refcount > 1) {
                const mixLabel = `${refcount} mixes`;
                const recentlyPrefix = timeMode > 0.9 ? 'recently ' : '';
                return (
                    <>
                        {seedBadge}
                        {stylePrefix}Played {recentlyPrefix}in{' '}
                        <span className="feed-inline-link" onClick={toggleInlineMixes}>{mixLabel}</span>
                    </>
                );
            }
            // refcount 0 and no mix title — still show the style alone if we have it.
            if (styleLabel) {
                return <>{seedBadge}<span className="feed-reason-style">{styleLabel}</span></>;
            }
        }
        if (item.type === 'played_together') {
            if (item.isAiRec) {
                return (<>Picked for you</>);
            }
            if (item.sourceTrack) {
                // Mobile: "Played with <track> by <DJ, DJ, DJ> in <Mix>" is three
                // names in a ~260px line and the mix — the tappable destination —
                // was always the part that fell off the ellipsis. Reduce to
                // "Played in <Mix>"; the mix page's tracklist shows the co-play.
                if (isMobileWebEnv && item.mixTitle) {
                    return <>Played in{' '}{renderMixLink()}{mixDateLabel}</>;
                }
                return (
                    <>
                        Played with{' '}
                        <EntityHoverCard
                            type="track"
                            entity={item.sourceTrack}
                            onNavigate={() => onSearchTrack({ stopPropagation: () => {} }, item.sourceTrack)}
                        >
                            <span className="feed-inline-link" onClick={(e) => { e.stopPropagation(); onSearchTrack(e, item.sourceTrack); }}>{item.sourceTrack.title}</span>
                        </EntityHoverCard>
                        {item.mixDj && (
                            <> by{' '}{renderDjCredit(item.mixDj)}</>
                        )}
                        {item.mixTitle && (
                            <> in{' '}{renderMixLink()}</>
                        )}
                        {mixDateLabel}
                    </>
                );
            }
        }
        if (item.type === 'trending') {
            const count = item.playedCount || 0;
            // Single mix in window — show "Played by <DJ> in <Mix> · <date>"
            // (linked, same as artist_played in For You).
            if (count === 1 && item.mixTitle) {
                // Mobile: the DJ isn't the reason on Trending, so drop the
                // credit and give the mix name the line.
                if (isMobileWebEnv) {
                    return <>Played in{' '}{renderMixLink()}{mixDateLabel}</>;
                }
                return (
                    <>
                        {item.mixDj ? (
                            <>
                                Played by{' '}
                                {renderDjCredit(item.mixDj)}
                                {' '}in{' '}
                            </>
                        ) : 'Played in '}
                        {renderMixLink()}
                        {mixDateLabel}
                    </>
                );
            }
            // Multiple mixes — clickable count expands an inline list of those mixes.
            if (count > 1) {
                return (
                    <>
                        Played recently in{' '}
                        <span className="feed-inline-link" onClick={toggleInlineMixes}>{`${count} mixes`}</span>
                    </>
                );
            }
            return null;
        }
        return item.reason;
    })();

    const triggerPlay = (e) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId }));
    };

    // On mobile web: tap the card body → play. Inline links + the
    // artwork still route to the track page via their own stopPropagation.
    const onCardClick = isMobileWebEnv ? (e) => {
        // If the click originated from an element that already handles
        // it (inline links, action buttons), its stopPropagation is
        // enough — this handler only fires when the blank card body
        // is tapped. Fire the same event the play button would.
        window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId }));
    } : undefined;

    return (
        <div
            className={`feed-card${isMobileWebEnv ? ' feed-card-mobile' : ''}`}
            onClick={onCardClick}
            role={isMobileWebEnv ? 'button' : undefined}
        >
            <div className="feed-card-body">
                <div
                    className={`feed-card-artwork${isMobileWebEnv ? '' : ' feed-clickable'}${coverPlaying ? ' playing' : ''}${coverPaused ? ' paused' : ''}`}
                    onClick={isMobileWebEnv
                        ? undefined
                        : (e) => { e.stopPropagation(); onSearchTrack(e, track); }}
                >
                    {art
                        ? <CoverArt src={art} size={FEED_ART_PX} alt={track.title} />
                        : <div className="feed-artwork-placeholder">&#9835;</div>
                    }
                    {isMobileWebEnv && (
                        <span className="feed-artwork-eq" aria-hidden="true">
                            <span></span><span></span><span></span><span></span>
                        </span>
                    )}
                </div>
                <div className="feed-card-content">
                    <div className="feed-card-track-info">
                        <DJPalAudioPreview
                            src={previewSrc}
                            youtubeId={track.youtubeId}
                            soundcloudUrl={track.soundcloudUrl}
                            trackId={track.id}
                            durationSec={track.durationSec ?? track.duration}
                            pageUrl={track.pageUrl}
                            previewId={previewId}
                            trackName={track.title}
                            trackArtist={track.artist}
                            trackArtists={track.artists}
                            trackCover={art}
                            playContext={playContext}
                            playbackBlocked={track.playbackBlocked}
                        >
                            <button className="feed-play-btn" onClick={triggerPlay}>
                                <svg className="feed-icon-play" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                                <svg className="feed-icon-pause" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="5" height="18" rx="1"/><rect x="14" y="3" width="5" height="18" rx="1"/></svg>
                            </button>
                        </DJPalAudioPreview>
                        <div className="feed-card-text">
                            <span
                                className={`feed-card-title${isMobileWebEnv ? '' : ' feed-clickable'}`}
                                onClick={isMobileWebEnv
                                    ? undefined
                                    : (e) => { e.stopPropagation(); onSearchTrack(e, track); }}
                            >
                                {track.title || 'Unknown Track'}
                            </span>
                            {(track.artist || (track.artists && track.artists.length > 0)) && (
                                <MultiArtistText
                                    artists={track.artists}
                                    fallbackText={track.artist}
                                    onSelect={(a) => onSearchArtist({ stopPropagation: () => {} }, a.name, a.id, a.artworkUrl)}
                                    className="feed-card-artist"
                                    segmentClassName="feed-clickable"
                                    segmentWrapper={artistHoverWrap}
                                />
                            )}
                        </div>
                    </div>
                    {/* The waveform is hidden by CSS on mobile, but mounting it
                        still fetches + AudioContext-decodes the sample audio
                        (peaksCache fallback) — pure waste on native + mobile
                        web. Don't mount it there at all. */}
                    {!isMobileWebEnv && (
                    <div
                        className={`feed-card-waveform-wrapper${waveformMode === 'FAKE' ? ' feed-card-waveform-mix' : ''}`}
                        onClick={waveformMode === 'FAKE' ? triggerPlay : undefined}
                    >
                        {/* FAKE mode: detailed synthetic silhouette underneath; the
                            real (full) waveform lazy-loads on play and fades in on
                            top, hiding the fake via the :has() rule in CSS. */}
                        {waveformMode === 'FAKE' && <FakeTrackWaveform seed={index + 1} />}
                        <FeedWaveform
                            src={previewSrc}
                            youtubeId={track.youtubeId}
                            soundcloudUrl={track.soundcloudUrl}
                            trackId={track.id}
                            index={index}
                            playbackBlocked={track.playbackBlocked}
                            mode={waveformMode}
                        />
                    </div>
                    )}
                </div>
            </div>
            <div className="feed-card-actions">
                <LikeButton track={track} />
                {isMobileWebEnv ? (
                    <FeedActionsKebab items={[
                        { label: 'Open in Mixpedia', icon: KebabIcon.mixpedia, onClick: (e) => onSearchTrack(e, track) },
                        { label: 'Copy track name', icon: KebabIcon.copy, onClick: (e) => onCopy(e, track, previewId) },
                        { label: 'Add to playlist', icon: KebabIcon.playlist, onClick: (e) => onAddToPlaylist(e, track) },
                        { label: 'Add to Next Up', icon: KebabIcon.queue, onClick: (e) => onAddToQueue(e, track) },
                        ...(onStartSet && track.title
                            ? [{ label: 'Start a set with this track', icon: KebabIcon.set, onClick: (e) => onStartSet(e, track) }]
                            : []),
                    ]} />
                ) : (
                    <>
                        <button type="button" className="feed-action-btn" title="Copy track name" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onCopy(e, track, previewId); }}>
                            {copiedId === previewId
                                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                            }
                        </button>
                        <button type="button" className="feed-action-btn" title="Add to playlist" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAddToPlaylist(e, track); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="11" x2="21" y2="11"/><line x1="3" y1="17" x2="12" y2="17"/><line x1="18" y1="14" x2="18" y2="22"/><line x1="14" y1="18" x2="22" y2="18"/></svg>
                        </button>
                        <button type="button" className="feed-action-btn feed-queue-btn" title="Add to Next Up" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAddToQueue(e, track); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                        </button>
                        {onStartSet && track.title && (
                            <button type="button" className="feed-action-btn" title="Start a set with this track" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onStartSet(e, track); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2.5"/><path d="M7.5 12 H20"/><path d="M16 8 l4 4 -4 4"/></svg>
                            </button>
                        )}
                    </>
                )}
                {/* Reason tag — same line as actions, pushed right */}
                <span className="feed-reason-inline">{reasonText}</span>
            </div>
            {popoverOpen && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    className="feed-mixes-popover"
                    style={{ top: popoverPos.top, bottom: popoverPos.bottom, left: popoverPos.left, width: popoverPos.width, maxHeight: popoverPos.maxH }}
                    onClick={(e) => e.stopPropagation()}
                    onScroll={onPopoverScroll}
                >
                    {inlineLoading && <div className="feed-mixes-popover-status">Loading mixes…</div>}
                    {!inlineLoading && inlineMixes !== null && inlineMixes.length === 0 && (
                        <div className="feed-mixes-popover-status">No mixes found</div>
                    )}
                    {inlineMixes && inlineMixes.map((mix, i) => {
                        const mixArt = mix.artworkUrl || mix.artwork || mix.image;
                        const mixDj = mix.djName || mix.artist;
                        const mixDate = mix.date ? formatFeedDate(mix.date) : '';
                        const handleOpen = (e) => {
                            e.stopPropagation();
                            setPopoverOpen(false);
                            onSearchMix(e, {
                                mixTitle: mix.title,
                                // publicEntityId, not entityId: a numeric id
                                // (stale snapshot / pre-08-12 shape) in a
                                // public_id filter finds nothing. Null here
                                // falls through to the mixLink branch.
                                mixPublicId: publicEntityId(mix),
                                mixLink: mix.link,
                            });
                        };
                        return (
                            <div
                                key={entityId(mix) || mix.link || i}
                                className="feed-mixes-popover-row"
                                onClick={handleOpen}
                            >
                                {mixArt
                                    ? <CoverArt src={mixArt} size={36} className="feed-mixes-popover-thumb" eager />
                                    : <div className="feed-mixes-popover-thumb feed-mixes-popover-thumb-placeholder">&#9835;</div>
                                }
                                <div className="feed-mixes-popover-meta">
                                    <span className="feed-mixes-popover-title">{mix.title || 'Untitled mix'}</span>
                                    {(mixDj || mixDate) && (
                                        <span className="feed-mixes-popover-sub">
                                            {mixDj && <>by {mixDj}</>}
                                            {mixDj && mixDate && ' · '}
                                            {mixDate}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {inlineLoadingMore && <div className="feed-mixes-popover-status">Loading more…</div>}
                    {!inlineLoading && !inlineLoadingMore && !allMixesLoaded && inlineMixes && inlineMixes.length > 0 && (
                        <button className="feed-mixes-popover-more" onClick={(e) => { e.stopPropagation(); loadMoreMixes(); }}>
                            Load more
                        </button>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
});

// Fake waveform matching the real FeedWaveform visual: 60px tall, white gradient,
// barWidth=2, barGap=2, natural-looking envelope. Seeded so each card differs.
// Note: stays fake — mixes have no preview audio so it never becomes a real waveform.
const FakeMixWaveform = React.memo(({ seed = 0 }) => {
    const BARS = 260;
    const H = 60;
    const barW = 2;
    const gap = 2;
    const width = BARS * (barW + gap);

    // Seeded PRNG
    let s = (seed * 9301 + 49297) % 233280;
    const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };

    // Real mix waveforms are flat: loudness-maxed with subtle energy drift.
    // Reach full height like the tracks feed; small sine + noise keep it alive.
    const phase1 = rand() * Math.PI * 2;
    const phase2 = rand() * Math.PI * 2;
    const bars = [];
    for (let i = 0; i < BARS; i++) {
        const t = i / BARS;
        const env = 0.80
            + 0.08 * Math.sin(t * Math.PI * 4 + phase1)
            + 0.04 * Math.sin(t * Math.PI * 11 + phase2);
        const noise = (rand() - 0.5) * 0.10;
        let h = env + noise;
        h = Math.max(0.45, Math.min(1.0, h));
        bars.push(h);
    }

    return (
        <svg
            className="feed-fake-waveform"
            viewBox={`0 0 ${width} ${H}`}
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={`mixwave-${seed}`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={H}>
                    <stop offset="0%" stopColor="rgba(255,255,255,0.75)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.5)" />
                </linearGradient>
            </defs>
            <path d={barsPath(bars.map((h) => h * H), H, barW, gap)} stroke={`url(#mixwave-${seed})`} strokeWidth={barW} strokeLinecap="round" fill="none" />
        </svg>
    );
});

// Helper: normalize mix.sources to arrays of {tracks, artists}.
// Current server shape: { includedTracks: [{artist,title}], includedArtists: [{id,name}] }.
// Legacy shape fallback keeps older cached feeds from rendering blank.
function normalizeSources(mix) {
    const s = mix.sources || {};
    const asArr = (v) => Array.isArray(v) ? v : [];
    let tracks = asArr(s.includedTracks);
    if (!tracks.length) {
        tracks = [...asArr(s.likedTracks), ...asArr(s.ownedTracks)];
    }
    let artists = asArr(s.includedArtists);
    if (!artists.length) {
        const followed = asArr(s.followedArtists).map(n => typeof n === 'string' ? { id: null, name: n } : n);
        const owned = asArr(s.ownedArtists).map(n => typeof n === 'string' ? { id: null, name: n } : n);
        const seen = new Set();
        artists = [...followed, ...owned].filter(a => {
            const k = (a?.name || '').toLowerCase();
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }
    return { tracks, artists, reason: s.reason || null };
}

// The strongest personalization signal behind a mix's matched tracks drives
// the chip wording + popover header. Server picks ONE per mix (id-based):
// liked/owned > artists you follow > artists you've liked a track of.
const REASON_NOUN = {
    liked_owned: 'tracks you like or own',
    followed: 'tracks by artists you follow',
    liked_artist: 'tracks by artists whose music you like',
};
const REASON_HEADER = {
    liked_owned: 'Tracks you like or own',
    followed: 'By artists you follow',
    liked_artist: 'By artists whose music you like',
};
const reasonNoun = (reason, n) => {
    const plural = REASON_NOUN[reason] || REASON_NOUN.liked_owned;
    return n === 1 ? plural.replace(/^tracks/, 'track') : plural;
};

// Rich track-reason chip: opens a popover of track rows (artwork + title /
// artist, like the rest of the app) with a "Show full tracklist" toggle.
// Artwork + resolved artists aren't in the feed payload (matched items are just
// {artist,title}), so on first open we fetch the mix's full tracklist once and
// use it both to enrich the matched rows and to power the full-list view.
const MixTrackSourceChip = ({ items, mix, reason, onPickTrack, onPickArtist }) => {
    const token = useSelector((state) => state.djpal.token);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const [tracklist, setTracklist] = useState(null);
    const ref = useRef(null);
    const dropdownRef = useRef(null);
    const fetchedRef = useRef(false);
    // Right-click on a tracklist row opens the same menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            const inTrigger = ref.current && ref.current.contains(e.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!inTrigger && !inDropdown) {
                setOpen(false);
                swallowNextClickOnMobile();
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useEffect(() => {
        if (!open || !ref.current) return;
        const updatePos = () => {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            // Open toward whichever side has more room, and cap the height to
            // that room so a long tracklist scrolls inside the viewport instead
            // of overflowing the top/bottom of the screen.
            const margin = 12;
            const spaceAbove = rect.top - margin;
            const spaceBelow = usableViewportBottom() - rect.bottom - margin;
            // Default down; flip up only when below can't fit the tallest
            // dropdown AND above is roomier.
            const openUp = spaceBelow < 360 && spaceAbove > spaceBelow;
            const maxH = Math.min(360, Math.max(140, Math.floor(openUp ? spaceAbove : spaceBelow)));
            setPos({ left: rect.right, top: openUp ? rect.top - 6 : rect.bottom + 6, openUp, maxH });
        };
        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
        };
    }, [open]);

    // The mix public id is exposed as `.id`; publicEntityId also covers
    // legacy shapes (publicId/public_id fields, numeric row ids from stale
    // snapshots — rejected). Some mixes only have a `link`. searchMixes
    // accepts public_id or link as the filter.
    const mixPid = publicEntityId(mix);
    const mixFilter = mixPid ? { public_id: mixPid } : (mix?.link ? { link: mix.link } : null);
    const canFetchTracklist = !!mixFilter && !!(window.electron?.djpal?.searchMixes);

    // Fetch the full tracklist once, when the popover first opens.
    useEffect(() => {
        if (!open || fetchedRef.current) return;
        if (!canFetchTracklist || !token) return;
        fetchedRef.current = true;
        let cancelled = false;
        const parseTracklist = (res) => {
            const data = (res && res.data) || res || {};
            const arr = data.results || data.result || (Array.isArray(data) ? data : null);
            const m = Array.isArray(arr) ? arr[0] : (data && (data.tracklist || data.tracks) ? data : null);
            const raw = (m && (m.tracklist || m.tracks)) || [];
            return Array.isArray(raw)
                ? raw.map((e) => (e && e.track) ? e.track : e).filter((t) => t && (t.title || t.artist))
                : [];
        };
        (async () => {
            try {
                let tl = parseTracklist(await window.electron.djpal.searchMixes(token, mixFilter));
                // The public_id filter sometimes returns the mix without its
                // tracklist; the link filter reliably includes it.
                if (!tl.length && mixPid && mix?.link) {
                    tl = parseTracklist(await window.electron.djpal.searchMixes(token, { link: mix.link }));
                }
                if (!cancelled) setTracklist(tl);
            } catch {
                if (!cancelled) setTracklist([]);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mix, token]);

    // 0 matched tracks is fine — we still offer a "Tracklist" trigger as long as
    // the mix's tracklist is fetchable. Only bail when there's truly nothing.
    if (!items.length && !canFetchTracklist) return null;

    // Normalize hard before matching (remix/version suffixes, punctuation, feat.)
    const normMatch = (s) => String(s || '')
        .toLowerCase()
        .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
        .replace(/\b(feat|ft|featuring|original mix|extended mix|radio edit)\.?\b/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    const buildNameLookup = (list) => {
        const byKey = new Map();
        (list || []).forEach((t) => {
            const ak = `${normMatch(t.artist)}|${normMatch(t.title)}`;
            if (ak !== '|' && !byKey.has(ak)) byKey.set(ak, t);
        });
        return (it) => byKey.get(`${normMatch(it.artist)}|${normMatch(it.title)}`) || null;
    };
    // Resolve each matched item to the FULL tracklist track (by id; then name) so
    // it carries playback fields + resolved artists for the row controls.
    const tracklistById = new Map((tracklist || []).filter((t) => t && t.id != null).map((t) => [t.id, t]));
    const byName = buildNameLookup(tracklist);
    const toTrack = (it) => tracklistById.get(it.id) || byName(it) || {
        id: it.id, title: it.title || '', artist: it.artist || '', artists: undefined, artworkUrl: it.artworkUrl || null,
    };

    // One unified list: YOUR matched tracks pinned on top, then the rest of the
    // set. The card headline ("features N of your tracks") keeps the
    // personalization; here we just surface those tracks first + the full set.
    const loadingRows = canFetchTracklist && tracklist === null;
    const matchedTracks = items.map(toTrack);
    const matchedIds = new Set(items.map((it) => it && it.id).filter((x) => x != null));
    const restTracks = (tracklist || []).filter((t) => t && !matchedIds.has(t.id));
    const combined = [...matchedTracks, ...restTracks];

    const pickTrack = (t) => { setOpen(false); if (t && t.title) onPickTrack(t); };
    const toggleOpen = (e) => { e.stopPropagation(); setOpen((v) => !v); };

    const renderRow = (t, idx) => {
        const art = t.artworkUrl || t.artwork || t.image || null;
        const src = t.previewUrl || t.sampleUrl || t.preview_url || null;
        return (
            <div key={`row-${t.id ?? idx}`} className="feed-mix-track-row" onContextMenu={openRowCtx(`row-${t.id ?? idx}`)}>
                <DJPalAudioPreview
                    src={src}
                    youtubeId={t.youtubeId}
                    soundcloudUrl={t.soundcloudUrl}
                    trackId={entityId(t)}
                    durationSec={t.durationSec ?? t.duration}
                    pageUrl={t.pageUrl}
                    previewId={`mixrow-${t.id ?? idx}`}
                    trackName={t.title}
                    trackArtist={t.artist}
                    trackArtists={t.artists}
                    trackCover={art}
                    playContext={{ items: combined, index: idx, type: 'tracks', origin: 'feed' }}
                    playbackBlocked={t.playbackBlocked}
                >
                    {art
                        ? <CoverArt className="feed-mix-track-art" src={art} size={40} alt="" />
                        : <span className="feed-mix-track-art feed-mix-track-art--ph">♫</span>}
                </DJPalAudioPreview>
                <div className="feed-mix-row-text">
                    <span
                        className="feed-mix-row-title feed-clickable"
                        onClick={(e) => { e.stopPropagation(); pickTrack(t); }}
                    >
                        {t.title}
                    </span>
                    {(t.artist || (t.artists && t.artists.length > 0)) && (
                        <MultiArtistText
                            artists={t.artists}
                            fallbackText={t.artist}
                            onSelect={(a) => { setOpen(false); onPickArtist(a); }}
                            className="feed-mix-row-artist"
                            segmentClassName="feed-clickable"
                        />
                    )}
                </div>
                <LikeButton track={t} />
                <TrackContextMenu track={t} desktopLike openAtPoint={rowCtxFor(`row-${t.id ?? idx}`)} />
            </div>
        );
    };

    const skeletonRows = (n) => Array.from({ length: n }).map((_, i) => (
        <div key={`sk-${i}`} className="feed-mix-track-row feed-mix-track-row--skeleton">
            <span className="feed-mix-track-art feed-mix-track-art--ph" />
            <span className="feed-mix-row-text">
                <span className="feed-mix-skeleton-line" style={{ width: '62%' }} />
                <span className="feed-mix-skeleton-line feed-mix-skeleton-line--sub" style={{ width: '42%' }} />
            </span>
        </div>
    ));

    return (
        <span className="feed-mix-source" ref={ref}>
            {items.length === 0 ? (
                <span className="feed-inline-link feed-mix-source-link" onClick={toggleOpen}>Tracklist</span>
            ) : (
                <>
                    features{' '}
                    <span className="feed-inline-link feed-mix-source-link" onClick={toggleOpen}>
                        {items.length} {reasonNoun(reason, items.length)}
                    </span>
                </>
            )}
            {open && pos && ReactDOM.createPortal(
                <div
                    ref={dropdownRef}
                    className="feed-mix-dropdown feed-mix-dropdown--rich"
                    style={{
                        left: pos.left,
                        top: pos.top,
                        maxHeight: pos.maxH,
                        transform: pos.openUp ? 'translate(-100%, -100%)' : 'translate(-100%, 0)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {loadingRows ? skeletonRows(items.length || 4) : (
                        <>
                            {matchedTracks.length > 0 && (
                                <>
                                    <div className="feed-mix-section">{REASON_HEADER[reason] || REASON_HEADER.liked_owned}</div>
                                    {matchedTracks.map((t, i) => renderRow(t, i))}
                                </>
                            )}
                            {restTracks.length > 0 && (
                                <>
                                    {matchedTracks.length > 0 && <div className="feed-mix-section">Tracklist</div>}
                                    {restTracks.map((t, i) => renderRow(t, matchedTracks.length + i))}
                                </>
                            )}
                            {matchedTracks.length === 0 && restTracks.length === 0 && (
                                <div className="feed-mix-dropdown-empty">Tracklist unavailable</div>
                            )}
                        </>
                    )}
                </div>,
                document.body
            )}
        </span>
    );
};

const MixFeedCard = React.memo(({ mix, index, onOpen, onCopy, copiedId, onPickTrack, onPickArtist, onPlay, onQueue, onAddToPlaylist, isLoading, isPlaying, playContext }) => {
    const v2 = useDesignV2();
    const mixDateLabel = mix.date ? feedDateLabel(mix.date, v2) : null;
    const previewId = `mix-${index}`;
    // Native (iOS+Android) may substitute the mix cover with the first track's
    // artwork (server flag); web/desktop/mobile-web get mix.artwork unchanged.
    useNativeMixCoverMode();
    const art = resolveMixCover(mix);
    const sources = normalizeSources(mix);
    // MixLikeButton keys on djName — aggregator emits `dj` so we shim a compatible shape.
    const djName = mix.djName || mix.dj || '';
    const likeShape = { ...mix, djName };

    // One reason chip per mix: "features N of your tracks" when the user has
    // tracks in it, otherwise a plain "Tracklist" trigger. Either way it opens
    // the same popover (your tracks pinned on top of the full set). The matched
    // *artists* reason is omitted — it's always the mix's own DJ, already shown.
    // The chip self-hides if there's no tracklist to fetch.
    const reasonText = (
        <MixTrackSourceChip
            items={sources.tracks}
            mix={mix}
            reason={sources.reason}
            onPickTrack={onPickTrack}
            onPickArtist={onPickArtist}
        />
    );

    const triggerOpen = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        onOpen(mix);
    };

    const triggerPlay = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        onPlay(mix, playContext);
    };

    const triggerQueue = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        onQueue(mix);
    };

    // A mix enters the playlist as a (very long) track-shaped row — same
    // shim MixContextMenu uses. Deliberately NO publicId: that field means a
    // music-info TRACK id downstream, a mix id there would poison it. The
    // mix identity rides its own mixPublicId field instead (this site was
    // missed when MixContextMenu got it — feed-card adds stored rows with no
    // identity at all, which rendered as unplayable not-found tracks).
    const triggerAddToPlaylist = (e) => {
        onAddToPlaylist(e, {
            title: mix.title || 'Unknown Mix',
            artist: djName,
            mixPublicId: publicEntityId(mix),
            artwork: art || mix.artwork || '',
            youtubeId: ytIdFromMixLink(mix.link),
            soundcloudUrl: (mix.link || '').includes('soundcloud.com') ? mix.link : null,
        });
    };

    return (
        <div className="feed-card feed-card-mix">
            <div className="feed-card-body">
                <div
                    className="feed-card-artwork feed-clickable"
                    onClick={triggerOpen}
                >
                    {art
                        ? <CoverArt src={art} size={FEED_ART_PX} alt={mix.title} />
                        : <div className="feed-artwork-placeholder">&#9835;</div>
                    }
                </div>
                <div className="feed-card-content">
                    <div className="feed-card-track-info">
                        {/* Native (iOS + Android): mixes aren't playable — no play icon (see canPlayMix). */}
                        {!isNative && (
                        <button
                            className={`feed-play-btn${isPlaying ? ' playing' : ''}`}
                            onClick={triggerPlay}
                            disabled={isLoading}
                            title={isPlaying ? 'Pause mix' : 'Play mix'}
                        >
                            {isLoading ? (
                                <svg className="feed-mix-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                                    <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
                                </svg>
                            ) : isPlaying ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="5" height="18" rx="1"/><rect x="14" y="3" width="5" height="18" rx="1"/></svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                            )}
                        </button>
                        )}
                        <div className="feed-card-text">
                            <span className="feed-card-title">
                                <span className="feed-clickable" onClick={triggerOpen}>
                                    {mix.title || 'Untitled Mix'}
                                </span>
                                {mixDateLabel && (
                                    <span className="feed-card-mix-date"> · {mixDateLabel}</span>
                                )}
                            </span>
                            {(mix.dj || (mix.artists && mix.artists.length > 0)) && (
                                <MultiArtistText
                                    artists={mix.artists}
                                    fallbackText={mix.dj}
                                    onSelect={(a) => onPickArtist(a)}
                                    className="feed-card-artist"
                                    segmentClassName="feed-clickable"
                                    segmentWrapper={(seg, node) => (
                                        <EntityHoverCard type="artist" entity={seg} onNavigate={() => onPickArtist(seg)}>
                                            {node}
                                        </EntityHoverCard>
                                    )}
                                />
                            )}
                        </div>
                    </div>
                    <div className="feed-card-waveform-wrapper feed-card-waveform-mix" onClick={triggerOpen}>
                        <FakeMixWaveform seed={index + 1} />
                        {mix.link && !mix.playbackBlocked && !isMobileWebEnv && (
                            <MixFeedWaveform
                                mixLink={mix.link}
                                mixId={entityId(mix)}
                                onPlay={() => onPlay(mix)}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="feed-card-actions">
                <MixLikeButton mix={likeShape} />
                {isMobileWebEnv ? (
                    <FeedActionsKebab items={[
                        { label: 'Open in Mixpedia', icon: KebabIcon.mixpedia, onClick: () => onOpen(mix) },
                        {
                            label: 'Copy mix name',
                            icon: KebabIcon.copy,
                            onClick: () => {
                                const text = mix.dj ? `${mix.dj} — ${mix.title}` : (mix.title || '');
                                onCopy(text, previewId);
                            },
                        },
                        { label: 'Add to Playlist', icon: KebabIcon.playlist, onClick: (e) => triggerAddToPlaylist(e) },
                        // Native (iOS + Android): mixes aren't playable, so "Add to Next Up" is omitted.
                        ...(!isNative ? [{ label: 'Add to Next Up', icon: KebabIcon.queue, onClick: triggerQueue }] : []),
                    ]} />
                ) : (
                    <>
                        <button
                            type="button"
                            className="feed-action-btn"
                            title="Copy mix name"
                            onClick={(e) => {
                                e.stopPropagation();
                                const text = mix.dj ? `${mix.dj} — ${mix.title}` : (mix.title || '');
                                onCopy(text, previewId);
                            }}
                        >
                            {copiedId === previewId
                                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                            }
                        </button>
                        <button type="button" className="feed-action-btn" title="Add to playlist" onClick={(e) => { e.stopPropagation(); e.preventDefault(); triggerAddToPlaylist(e); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="11" x2="21" y2="11"/><line x1="3" y1="17" x2="12" y2="17"/><line x1="18" y1="14" x2="18" y2="22"/><line x1="14" y1="18" x2="22" y2="18"/></svg>
                        </button>
                        {!isNative && (
                        <button type="button" className="feed-action-btn feed-queue-btn" title="Add to Next Up" onClick={triggerQueue} disabled={isLoading}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                        </button>
                        )}
                    </>
                )}
                {reasonText && <span className="feed-reason-inline">{reasonText}</span>}
            </div>
        </div>
    );
});

// For You on the free plan: the first N items render as real cards, the rest
// sit behind this wall — blurred card shapes fading out under the real locked
// count and the upgrade CTA. The list was already cut by main /
// music-info-server (feedFreeTaste.js); this only renders what they reported
// as missing, so nothing here can be un-blurred into real content.
const FEED_WALL_COPY = {
    tracks: (n, shown) => [
        `GOAT found ${n.toLocaleString()} more track${n === 1 ? '' : 's'} for you`,
        `Free plan shows ${shown} picks a day. Unlock the whole feed — every DJ you follow, every set that plays your likes.`,
    ],
    mixes: (n, shown) => [
        `${n.toLocaleString()} more set${n === 1 ? '' : 's'} built around your taste`,
        `Free plan shows ${shown} a day. GOAT unlocks every mix For You finds.`,
    ],
    trending: (n, shown) => [
        `${n.toLocaleString()} more track${n === 1 ? '' : 's'} heating up right now`,
        `Free plan shows the top ${shown} a day. GOAT unlocks the full trending list.`,
    ],
};

const FeedFreeWall = ({ list, shown, moreCount, onUnlock }) => {
    const [title, sub] = (FEED_WALL_COPY[list] || FEED_WALL_COPY.tracks)(moreCount, shown);
    return (
        <div className="feed-free-wall" data-list={list} data-more={moreCount}>
            <div className="feed-free-wall-cards" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="feed-skeleton-card feed-free-wall-card">
                        <div className="feed-skeleton-art" />
                        <div className="feed-skeleton-content">
                            <div className="feed-skeleton-line short" />
                            <div className="feed-skeleton-line" />
                            <div className="feed-skeleton-wave" />
                        </div>
                    </div>
                ))}
            </div>
            <div className="feed-free-wall-overlay">
                <div className="feed-free-wall-text">
                    <strong>{title}</strong>
                    <span>{sub}</span>
                </div>
                <button type="button" className="feed-free-wall-btn" onClick={onUnlock}>Unlock with <GoatBadge /></button>
            </div>
        </div>
    );
};

const FeedTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [copiedTrackId, setCopiedTrackId] = useState(null);
    const [playlistTrack, setPlaylistTrack] = useState(null);
    const subTab = useSelector((state) => state.feed.subTab);
    const { requireUpgrade } = useTierGate('feed');

    const { items, mixes, isLoading, isLoadingMore, hasMore, error, lastFetchedAt } = useSelector((state) => state.feed);
    const freeLimit = useSelector((state) => state.feed.freeLimit);
    const trendingFreeLimit = useSelector((state) => state.feed.trendingFreeLimit);
    const tasteNotice = useSelector((state) => state.feed.tasteNotice);
    const goatCut = useSelector((state) => state.feed.goatCut?.[subTab === 'trending' ? 'trending' : 'feed']);
    // The notice explains one click; it should not sit there forever.
    useEffect(() => {
        if (!tasteNotice) return undefined;
        const t = setTimeout(() => dispatch(setTasteNotice(null)), 9000);
        return () => clearTimeout(t);
    }, [tasteNotice, dispatch]);
    const store = useStore();
    const trendingItems = useSelector((state) => state.feed.trendingItems || []);
    const trendingIsLoading = useSelector((state) => state.feed.trendingIsLoading);
    const trendingFetchedAt = useSelector((state) => state.feed.trendingFetchedAt);
    const trendingError = useSelector((state) => state.feed.trendingError);
    const { playMix, queueMix, loadingMixId, playingMixId } = useMixPlayback();
    const tokenValid = useSelector((state) => state.djpal.tokenValid);
    const tokenError = useSelector((state) => state.djpal.tokenError);

    // Pre-fetch the first playable feed track's audio once the feed lands, so
    // the user's first play is instant (no extraction — the click hits the
    // cached file). Also warms deno + the nsig cache for other tracks. Desktop-
    // only — guarded by window.electron, so it's a no-op on web/mobile.
    const _warmedRef = useRef(false);
    useEffect(() => {
        if (_warmedRef.current || !window.electron?.djpal?.warmStream) return;
        const pool = (items && items.length ? items : trendingItems) || [];
        const first = pool.find((it) => it && (it.youtubeId || it.soundcloudUrl));
        if (!first) return;
        const url = first.youtubeId
            ? `https://www.youtube.com/watch?v=${first.youtubeId}`
            : first.soundcloudUrl;
        if (!url) return;
        _warmedRef.current = true;
        window.electron.djpal.warmStream(url);
    }, [items, trendingItems]);

    // Personalization signal: user has nothing for the For You feed to work
    // with. Drives (1) trending banner copy and (2) auto-switching to trending
    // on first paint when there's nothing personalized to show.
    const likesCount = useSelector((state) => (state.likes?.likedTracks?.length || state.likes?.total || 0));
    const followsCount = useSelector((state) => (state.followedArtists?.total || 0));
    // Whether we've actually fetched the like/follow counts this session.
    // They sit at 0 until their sidebar is first opened, which made the
    // personalize nudge fire on a stale 0 (showing it to users with lots of
    // likes) and then vanish the moment the user opened Likes. We now load
    // the counts on feed mount and gate the nudge on these flags.
    const likesLoaded = useSelector((state) => !!state.likes?.loaded);
    const followsLoaded = useSelector((state) => !!state.followedArtists?.loaded);
    const hasNoPersonalData = likesCount === 0 && followsCount === 0
        && (lastFetchedAt != null && items.length === 0);
    // "Make it personal" nudge on the Tracks/Mixes sub-tabs: shown when the user
    // has no / barely any personalization signals, dismissable (persisted).
    // Dismissal snoozes the nudge for 2 days (stores the dismiss timestamp), not
    // forever — so a user who's still anonymous a couple days later gets a gentle
    // reminder instead of never seeing it again.
    const NUDGE_SNOOZE_MS = 2 * 24 * 60 * 60 * 1000;
    const [personalizeNudgeDismissed, setPersonalizeNudgeDismissed] = useState(() => {
        try {
            const at = parseInt(localStorage.getItem('mg:feed:personalizeNudgeDismissedAt') || '0', 10);
            return at > 0 && (Date.now() - at) < NUDGE_SNOOZE_MS;
        } catch { return false; }
    });
    const dismissPersonalizeNudge = useCallback(() => {
        setPersonalizeNudgeDismissed(true);
        try { localStorage.setItem('mg:feed:personalizeNudgeDismissedAt', String(Date.now())); } catch { /* non-fatal */ }
    }, [NUDGE_SNOOZE_MS]);

    // Load like/follow counts once on feed mount so the personalize nudge has
    // real numbers to gate on. Previously these were only fetched when their
    // sidebars opened, so the count read 0 on a fresh login — the nudge fired
    // for users who actually have plenty of likes, then disappeared the moment
    // they clicked into Likes (which loaded the real count). Guarded on the
    // `loaded` flag so it's a one-shot per session, not a refetch every mount.
    useEffect(() => {
        if (tokenValid !== true) return;
        if (!likesLoaded) dispatch(fetchLikes());
        if (!followsLoaded) dispatch(fetchFollowedArtists());
    }, [tokenValid, likesLoaded, followsLoaded, dispatch]);
    const [visibleCount, setVisibleCount] = useState(() => _savedVisibleCount[subTab] || 6);
    const [pendingRestore, setPendingRestore] = useState(true);
    const timeMode = useSelector((state) => state.feed.timeMode ?? 1);
    const surpriseActive = useSelector((state) => state.feed.surpriseActive);
    // Server-reported share (0–1) of the last feed that was random discovery vs.
    // personalized. It already folds in signal count, material, and the slider
    // bucket — so a high value is the honest "this feed is mostly discovery"
    // signal, the right trigger for the personalize nudge (zero AND low signal).
    const discovery = useSelector((state) => state.feed.discovery || 0);
    const [sliderValue, setSliderValue] = useState(timeMode);

    // Background DJPal token resolution — see useDjpalConnect for rationale.
    // (Root-cause fix for "the feed won't load until I reload the app": the
    // old fire-once effect never retried after a cold-start auth race.)
    const { connectFailed, retry: retryConnect } = useDjpalConnect();

    // Sub-tab landing: always start on the default Tracks sub-tab (set in the
    // feed slice's initial state). We used to flip to Trending before paint
    // while the For You fetch was in flight and then yank back to Tracks once
    // items arrived, but that "Trending → Tracks" jump read as a glitch — so
    // we just land on Tracks and let the feed hydrate in place.

    // Web-only: nudge users to sync their library on desktop for a personalized feed
    useEffect(() => {
        if (!isWeb) return;
        const timer = setTimeout(() => {
            notifyWebOnly('feed', 'hint');
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const likesTotal = useSelector((state) => state.likes.total);
    const followsTotal = useSelector((state) => state.followedArtists.total);
    // True once the counts have actually been fetched this session. Before
    // that both totals read 0 — which looked like "low-density user whose
    // totals changed" and FORCED a full server rebuild (5–12s) on every cold
    // app start on the phones (2026-08-17: every native open logged
    // cacheStatus=force). Unknown totals must never decide force.
    const totalsKnown = likesLoaded && followsLoaded; // (selectors declared above)
    const lastFetchedLikesTotal = useSelector((state) => state.feed.lastFetchedLikesTotal);
    const lastFetchedFollowsTotal = useSelector((state) => state.feed.lastFetchedFollowsTotal);

    // Best-known signal count for the personalize nudge: take the larger of
    // the live count and the total the server saw when it built the feed, so
    // a user with plenty of likes never trips the "< 25" nudge regardless of
    // which source has hydrated first.
    const knownSignalCount = Math.max(likesCount, lastFetchedLikesTotal || 0)
        + Math.max(followsCount, lastFetchedFollowsTotal || 0);
    // Only trust the count once we've actually loaded it (or the server
    // already reported a non-zero total at fetch time). Prevents the nudge
    // from flashing on a stale 0 before the mount fetch lands.
    const signalCountKnown = (likesLoaded && followsLoaded)
        || (lastFetchedLikesTotal || 0) > 0 || (lastFetchedFollowsTotal || 0) > 0;

    const searchTerm = useSelector((state) => state.app.searchTerm);
    const matchIndexRef = useRef(0);

    // Find-in-page: walk the rendered cards for the term at call time.
    const findMatchCards = useCallback(() => {
        const container = scrollRef.current;
        if (!container || !searchTerm) return [];
        const term = searchTerm.toLowerCase();
        const cards = [];
        for (const card of container.querySelectorAll('.feed-card')) {
            if (card.textContent.toLowerCase().includes(term)) cards.push(card);
        }
        return cards;
    }, [searchTerm]);

    // Auto-jump to the first match as the user types — matches real browser
    // find rather than waiting for Enter. Deferred a frame so freshly-
    // rendered cards are in the DOM first.
    useEffect(() => {
        matchIndexRef.current = 0;
        if (!searchTerm) return;
        const raf = requestAnimationFrame(() => {
            findMatchCards()[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return () => cancelAnimationFrame(raf);
    }, [searchTerm, findMatchCards]);

    // Enter / Shift+Enter step to the next/prev match.
    useEffect(() => {
        const handleNextMatch = (e) => {
            const matchEls = findMatchCards();
            if (!matchEls.length) return;
            matchIndexRef.current = (matchIndexRef.current + e.detail + matchEls.length) % matchEls.length;
            matchEls[matchIndexRef.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.addEventListener('search-next-match', handleNextMatch);
        return () => window.removeEventListener('search-next-match', handleNextMatch);
    }, [findMatchCards]);

    // useLayoutEffect (not useEffect): the clear-to-skeletons decision must
    // run BEFORE the browser paints — with useEffect the cached previous
    // feed flashed for a frame before the skeletons replaced it.
    useLayoutEffect(() => {
        if (tokenValid !== true) return;
        // In-session stability: don't auto-refetch when we already have a
        // healthy feed. The previous full-stop early-return was too strict
        // — it blocked recovery from transient errors (e.g. a 502 mid-fetch
        // from a deploy bounce), leaving the user stuck on "Nothing here
        // yet" with no way out. New gate: only skip if we've already
        // fetched AND items are present. An empty feed means either
        // first-mount or unrecovered error; either way, fetching is right.
        const haveHealthyFeed = _hasFetchedOnce && items.length > 0;
        if (haveHealthyFeed) return;
        const stale = !lastFetchedAt || (Date.now() - lastFetchedAt > 5 * 60 * 1000);
        // Both sides must be known: the feed may have been fetched before
        // likes/follows loaded (lastFetched* === null) — comparing that
        // against the real counts is not a change, it's missing data.
        const fetchedTotalsKnown = lastFetchedLikesTotal != null && lastFetchedFollowsTotal != null;
        const totalsChanged = totalsKnown && fetchedTotalsKnown
            && (likesTotal !== lastFetchedLikesTotal || followsTotal !== lastFetchedFollowsTotal);
        // First mount uses (stale || totalsChanged) as today. Recovery path
        // (items empty after a fetch attempt) also enters — we want to
        // retry regardless of the stale check.
        if (stale || totalsChanged || (_hasFetchedOnce && items.length === 0)) {
            if (!isLoading) {
                // When a refresh IS happening, show skeletons — never render
                // the previous feed and then swap every card a second later
                // (the "whole feed silently replaced itself" effect). The
                // stale-while-revalidate variant that kept old items on
                // screen was explicitly rejected in QA.
                resetWaveformQueue();
                dispatch(clearFeed());
                // For low-density users (< 25 signals), force-bypass the
                // server pool cache when totals changed since the last
                // fetch — surfaces fresh likes/follows on page reload.
                // Power users skip the force: a single new signal has
                // marginal effect on a deep graph.
                const isLowDensity = totalsKnown && (likesTotal + followsTotal) < 25;
                dispatch(fetchFeed({ force: totalsChanged && isLowDensity }));
            }
        }
        _hasFetchedOnce = true;
    }, [tokenValid, likesTotal, followsTotal, totalsKnown, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch trending when the user opens the Trending sub-tab (lazy + cached
    // server-side for 30 min, so re-entering rarely actually re-runs the query).
    useEffect(() => {
        if (subTab !== 'trending') return;
        if (tokenValid !== true) return;
        if (trendingIsLoading) return;
        const stale = !trendingFetchedAt || (Date.now() - trendingFetchedAt > 30 * 60 * 1000);
        if (trendingItems.length === 0 || stale) {
            // Same rule as For You: never keep the previous list on screen
            // and swap it under the user when the refetch lands — clear to
            // skeletons first. Re-entries inside the 30-min cache window
            // don't refetch at all, so this only fires when content may
            // genuinely have changed.
            if (trendingItems.length > 0) dispatch(setTrendingItems([]));
            dispatch(fetchTrendingFeed({ windowDays: 90, limit: 500 }));
        }
    }, [subTab, tokenValid]); // eslint-disable-line react-hooks/exhaustive-deps

    // Visibility-change auto-refetch of a HEALTHY feed was removed (it
    // counted as "auto-refresh without user interaction" in QA — the user
    // hits refresh manually if they want fresh data). The one exception is
    // recovery: coming back to the app (phone resume, tab refocus) with an
    // EMPTY or ERRORED For You — a fetch that died mid-session while the
    // app was backgrounded (frozen timers, dropped radio, expired token)
    // otherwise leaves "Nothing here yet" on screen with no way out except
    // the refresh button. Populated feeds are never touched here.
    useEffect(() => {
        if (tokenValid !== true) return undefined;
        let last = 0;
        const recover = (evt) => {
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            // `online` is a distinct signal (radio came back after a failed
            // resume fetch) — never debounce it against the focus burst that
            // preceded it, or the one event that could heal the feed is dropped.
            if (evt?.type !== 'online' && now - last < 10_000) return; // focus + visibilitychange burst
            last = now;
            const st = store.getState().feed;
            if (st.isLoading) return;
            // A legitimately empty feed (brand-new user: no likes/follows,
            // server returned 0) isn't broken — don't hammer it on refocus.
            const legitimatelyEmpty = !st.error && st.items.length === 0 && st.lastFetchedAt
                && (store.getState().likes?.total || 0) === 0
                && (store.getState().followedArtists?.total || 0) === 0;
            const broken = !!st.error || (st.items.length === 0 && !legitimatelyEmpty);
            if (!broken) return;
            resetWaveformQueue();
            dispatch(clearFeed());
            dispatch(fetchFeed());
        };
        window.addEventListener('focus', recover);
        window.addEventListener('online', recover);
        document.addEventListener('visibilitychange', recover);
        return () => {
            window.removeEventListener('focus', recover);
            window.removeEventListener('online', recover);
            document.removeEventListener('visibilitychange', recover);
        };
    }, [tokenValid]); // eslint-disable-line react-hooks/exhaustive-deps

    const scrollRef = useRef(null);

    // Header reveal (Instagram/Twitter style): the header tracks the scroll
    // delta 1:1, so scrolling DOWN slides it away exactly like normal content
    // (no "sticky then gone" snap), and scrolling UP slides it back in at the
    // same rate — even deep in the feed. No transition: it follows the finger.
    const headerRef = useRef(null);
    const lastFeedScrollRef = useRef(0);
    const headerOffsetRef = useRef(0);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        // Texture native size (black_background_3.png). The page (.tab-content)
        // paints it center/cover; we replicate that exact crop on the header so
        // its background lines up with the page (no seam) — see FeedTab.scss.
        const TW = 2880, TH = 2048;
        let params = null;
        const computeParams = () => {
            const header = headerRef.current;
            const tc = header && header.closest('.tab-content');
            if (!header || !tc) return;
            const tr = tc.getBoundingClientRect();
            if (!tr.width || !tr.height) return;
            const scale = Math.max(tr.width / TW, tr.height / TH);
            const rw = TW * scale, rh = TH * scale;
            params = { rw, rh, texLeft: tr.left + tr.width / 2 - rw / 2, texTop: tr.top + tr.height / 2 - rh / 2 };
            header.style.backgroundSize = `${rw}px ${rh}px`;
        };
        const alignBg = () => {
            const header = headerRef.current;
            if (!header || !params) return;
            const hr = header.getBoundingClientRect();
            header.style.backgroundPosition = `${Math.round(params.texLeft - hr.left)}px ${Math.round(params.texTop - hr.top)}px`;
        };
        const onScroll = () => {
            const header = headerRef.current;
            if (!header) return;
            const y = el.scrollTop;
            const delta = y - lastFeedScrollRef.current;
            lastFeedScrollRef.current = y;
            const h = header.offsetHeight || 1;
            let off = headerOffsetRef.current - delta;   // down → up; up → down
            if (off > 0 || y <= 0) off = 0;              // fully shown at/above the top
            else if (off < -(h + 2)) off = -(h + 2);     // fully hidden (+2px to clear the underline at the clip edge)
            headerOffsetRef.current = off;
            header.style.transform = off ? `translateY(${Math.round(off)}px)` : '';
            // DESIGN V2 mobile: the header is transparent at rest (it sits in the
            // ambient glow) and glass once it is pinned over the list. Classic
            // has no rule for the class, so it is inert there.
            header.classList.toggle('is-pinned', y > 0);
            alignBg();
        };
        // Initial alignment: retry across frames until the header + page are laid
        // out (the header renders only once the feed has content).
        let raf, tries = 0;
        const init = () => {
            computeParams();
            alignBg();
            if (!params && tries++ < 90) raf = requestAnimationFrame(init);
        };
        init();
        const onResize = () => { computeParams(); alignBg(); };
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    // Re-tapping the already-active "For You" bottom-nav tab scrolls to top.
    useEffect(() => {
        const onRetap = (e) => {
            if (e?.detail?.key !== 'feed') return;
            const el = scrollRef.current;
            if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
        };
        window.addEventListener('mixgoat:tab-retap', onRetap);
        return () => window.removeEventListener('mixgoat:tab-retap', onRetap);
    }, []);

    const visibleCountRef = useRef(visibleCount);
    visibleCountRef.current = visibleCount;
    const subTabRef = useRef(subTab);

    const setSubTab = useCallback((tab) => {
        dispatch(setFeedSubTab(tab));
    }, [dispatch]);

    // When sub-tab changes (via user click OR nav-history back/forward), save the
    // OLD sub-tab's scroll + visible count and load the NEW sub-tab's. Runs AFTER
    // render, but before any new scroll event for the new tab — so el.scrollTop
    // still reflects the old tab's final position.
    useEffect(() => {
        if (subTabRef.current === subTab) return;
        const prev = subTabRef.current;
        const el = scrollRef.current;
        if (el) _savedScrollTop[prev] = el.scrollTop;
        _savedVisibleCount[prev] = visibleCountRef.current;
        subTabRef.current = subTab;
        setVisibleCount(_savedVisibleCount[subTab] || 6);
        setPendingRestore(true);
    }, [subTab]);

    // Restore scroll. Re-runs until the container has enough scroll range to
    // reach the target — handles the case where items haven't rendered yet on
    // mount (e.g., stale refetch clears the list before populating it).
    // useLayoutEffect, not useEffect: with useEffect the list painted from the
    // top for a frame and THEN jumped to the saved position — on re-entering
    // For You after scrolling it looked like the whole feed swapped itself
    // for a different set of tracks.
    useLayoutEffect(() => {
        if (!pendingRestore) return;
        const el = scrollRef.current;
        if (!el) return;
        const target = _savedScrollTop[subTab] || 0;
        if (target <= 0) {
            setPendingRestore(false);
            return;
        }
        if (el.scrollHeight - el.clientHeight >= target) {
            el.scrollTop = target;
            setPendingRestore(false);
        }
    }, [pendingRestore, items, mixes, visibleCount, subTab]);

    // Save on unmount (scroll handler already saves on each scroll, but unmount
    // without a trailing scroll event would lose the last position).
    useEffect(() => {
        return () => {
            const el = scrollRef.current;
            if (el) _savedScrollTop[subTabRef.current] = el.scrollTop;
            _savedVisibleCount[subTabRef.current] = visibleCountRef.current;
        };
    }, []);

    // Infinite scroll — trigger early, no cooldown (isLoadingMore prevents double-fire)
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleScroll = () => {
            // Persist per-sub-tab scroll position for restoration across tab/sub-tab switches
            _savedScrollTop[subTab] = el.scrollTop;

            if (isLoading) return;
            // Source length is per-subTab. Without the trending branch the
            // handler gated on For-You items.length, so a new user with an
            // empty For-You feed would never grow visibleCount on the
            // trending tab — the user saw only the initial 6 cards.
            const sourceLen = subTab === 'mixes' ? mixes.length
                : subTab === 'trending' ? trendingItems.length
                : items.length;
            if (sourceLen === 0) return;
            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 800;
            if (nearBottom) {
                setVisibleCount(prev => {
                    const next = prev + 6;
                    // Only tracks have server-side pagination for now
                    if (subTab === 'tracks' && next >= items.length - 3 && !isLoadingMore && hasMore) {
                        dispatch(fetchMoreFeed());
                    }
                    return next;
                });
            }
        };
        el.addEventListener('scroll', handleScroll, { passive: true });
        // Growth is scroll-driven, so when the first page fits the viewport
        // (wide window, few hero rows) nothing could ever scroll and the feed
        // stalled at six cards. Run the same check once per layout.
        const raf = requestAnimationFrame(handleScroll);
        return () => { el.removeEventListener('scroll', handleScroll); cancelAnimationFrame(raf); };
    }, [dispatch, items.length, mixes.length, trendingItems.length, isLoading, isLoadingMore, hasMore, subTab, visibleCount]);

    // [REMOVED] Feed auto-prequeue.
    //
    // Previously, when a feed track started playing this effect would
    // queue the next 5 feed tracks with their SAMPLE URLs (preview_url) as
    // `musicSrc`. The intent was "make next instant" — jinke could step
    // through pre-queued items without waiting on extraction.
    //
    // Side effects that broke the lazy-resolve flow:
    //   1. After 5 advance-prequeue events, audioList had 6 items. The
    //      next-button hijack saw `isLast=false` and bailed, letting jinke
    //      step into the next pre-queued sample directly — bypassing
    //      advanceListContext + resolveTrackSrc entirely. Sample-only
    //      tracks would then play their preview forever (no upgrade).
    //   2. For tracks with a known youtubeId/soundcloudUrl, MusicPlayer's
    //      onAudioPlay upgrade swapped audio.src to YT mid-play after ~2s,
    //      producing the jarring "sample for 2s then full track" UX the
    //      user reported.
    //
    // The advanceListContext flow (MusicPlayer.js:1097) already handles
    // "what's next" correctly: it calls resolveTrackSrc on the next feed
    // item, which fires lazy resolve when needed and queues the full-play
    // URL directly — no upgrade-mid-play, no sample-only stuck state.
    // Falling through to that path produces consistent behavior across
    // tracks regardless of whether their YT id is known a priori.

    // [REMOVED 2026-08-20] Feed "Play All" button + handler. Redundant by
    // construction: clicking any feed card already hands the entire feed to
    // the listContext and auto-advances through it, so Play All duplicated
    // "click the first card" via a second resolve path that had to be kept in
    // sync (and wasn't — it shipped with no loading feedback). No major
    // player (Spotify/Apple/YouTube Music/SoundCloud) puts collection-play on
    // a discovery feed; rows are the entry point. Collection surfaces (likes,
    // playlists, mix tracklists, search lists) keep their stateful Play All.

    const handleRefresh = useCallback(() => {
        resetWaveformQueue();
        if (subTab === 'trending') {
            // Trending lives in its own slice — clearFeed/fetchFeed wouldn't
            // touch it. Force-bypass the server cache so a stale empty entry
            // (e.g. from a transient DB miss) doesn't outlive the user's
            // refresh click. Clear to skeletons first, same as For You's
            // refresh — a forced fetch can genuinely change the list, and
            // swapping it under the user was the effect QA rejected.
            dispatch(setTrendingItems([]));
            dispatch(fetchTrendingFeed({ windowDays: 90, limit: 100, force: true }));
        } else if (surpriseActive) {
            // In Surprise mode, Refresh re-rolls a NEW random batch (stays on).
            dispatch(clearFeed());
            dispatch(fetchFeed({ surprise: true }));
        } else {
            dispatch(clearFeed());
            dispatch(fetchFeed({ force: true }));
        }
    }, [dispatch, subTab, surpriseActive]);

    // GOAT user cut to the free list: re-resolve the plan, then reload this list.
    const handleGoatCutRetry = useCallback(async () => {
        trackEvent('feed_goat_cut_retried', { list: subTab === 'trending' ? 'trending' : 'feed' });
        try { await dispatch(refreshTier()); } catch { /* the reload below says what the server thinks */ }
        handleRefresh();
    }, [dispatch, subTab, handleRefresh]);

    // Surprise Me is a TOGGLE: off → a random, taste-ignoring feed within the
    // current window; reclicking while active returns to the personalized feed.
    // (For a *new* random batch while active, use Refresh — it re-rolls.)
    const handleSurprise = useCallback(() => {
        resetWaveformQueue();
        dispatch(clearFeed());
        dispatch(fetchFeed(surpriseActive ? {} : { surprise: true }));
    }, [dispatch, surpriseActive]);

    const handleSliderChange = useCallback((e) => {
        setSliderValue(parseFloat(e.target.value));
    }, []);

    const handleSliderRelease = useCallback((e) => {
        const val = parseFloat(e.target.value);
        if (Math.abs(val - timeMode) < 0.05) return;
        dispatch(setTimeMode(val));
        resetWaveformQueue();
        dispatch(clearFeed());
        // Force a rebuild on release so the feed immediately reflects the new
        // window — no manual Refresh needed. (A non-force request can hit a
        // stale per-timeMode cache slot built before the window logic changed,
        // which is exactly the "moved the slider but still see old dates" bug.)
        dispatch(fetchFeed({ force: true }));
    }, [dispatch, timeMode]);

    const handleCopyTrack = useCallback((e, track, trackId) => {
        e.stopPropagation();
        e.preventDefault();
        const text = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedTrackId(trackId);
        setTimeout(() => setCopiedTrackId(null), 1500);
    }, []);

    const handleCopyMix = useCallback((text, id) => {
        if (!text) return;
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedTrackId(id);
        setTimeout(() => setCopiedTrackId(null), 1500);
    }, []);

    const handleSearchTrack = useCallback((e, track) => {
        e.stopPropagation();
        const query = track.title && track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        dispatch(setTrackQuery(query));
        dispatch(setActiveSearch('track'));
        dispatch(setActiveTab('djpal'));
        dispatch(searchTrack({ trackName: query, trackId: entityId(track), variant: 'A', artworkUrl: track.artwork || track.artworkUrl || track.image || null }));
    }, [dispatch]);

    const handleSearchArtist = useCallback((e, artistName, artistId = null, artworkUrl = null) => {
        e.stopPropagation();
        dispatch(setArtistQuery(artistName));
        dispatch(setActiveSearch('artist'));
        dispatch(setActiveTab('djpal'));
        dispatch(searchArtist({ artistName, artistId, artworkUrl: artworkUrl || null }));
    }, [dispatch]);


    const handleSearchMix = useCallback((e, item) => {
        e.stopPropagation();
        // Id-first, no text-search fallback: feed rows with a mix context
        // always ship mixPublicId (feedAssembler), and mixLink is an exact
        // unique key for the rest. Clicks navigate by id; only the search
        // box searches by text.
        const filter = item.mixPublicId
            ? { public_id: item.mixPublicId }
            : item.mixLink
                ? { link: item.mixLink }
                : null;
        if (!filter) return;
        const mixTitle = item.mixTitle || '';
        dispatch(setMixQuery(mixTitle));
        dispatch(setActiveSearch('mix'));
        dispatch(setActiveTab('djpal'));
        dispatch(searchMixes({ filters: filter }));
    }, [dispatch]);

    const handleOpenMix = useCallback((mix) => {
        handleSearchMix({ stopPropagation: () => {} }, {
            mixTitle: mix.title,
            mixPublicId: publicEntityId(mix),
            mixLink: mix.link,
        });
    }, [handleSearchMix]);

    // NEVER overwrite mix.id here: `id` must stay the real catalog id (or
    // absent). playMix/queueMix key their loading/playing state on
    // mixTrackingKey internally — the old id-synthesis leaked URLs/titles
    // into the queue entry's mixId and broke id-based navigation from the
    // player bar. Only normalize djName.
    const withMixId = useCallback((mix) => ({
        ...mix,
        djName: mix.djName || mix.dj || '',
    }), []);

    const handlePlayMix = useCallback((mix, playContext = null) => {
        if (isWeb) { notifyWebOnly('mix'); return; }
        playMix(withMixId(mix), playContext);
    }, [playMix, withMixId, isWeb]);

    const handleQueueMix = useCallback((mix) => {
        if (isWeb) { notifyWebOnly('mix'); return; }
        queueMix(withMixId(mix));
    }, [queueMix, withMixId]);

    const handleAddToQueue = useCallback(async (e, track) => {
        e.stopPropagation();
        e.preventDefault();
        // Adding to the queue must never START playback: when the queue is
        // empty and nothing is playing, a lingering autoPlay=true from an
        // earlier session makes jinke play the first added track immediately.
        dispatch((d, getState) => {
            const mp = getState().musicPlayer;
            const audio = window.musicPlayerInstance;
            const playingNow = !!(audio && audio.src && !audio.paused);
            if ((mp.audioList?.length || 0) === 0 && !playingNow && mp.autoPlay) d(setAutoPlay(false));
            // Register the feed as the ambient context when none is active:
            // a queue built straight from For You (without ever clicking play
            // on a feed row) must still resume into the feed when it drains
            // or a track fails — otherwise playback just stops.
            const ctx = getState().listContext;
            if (!ctx?.type || !ctx.items?.length) {
                const trackItems = (items || []).map(it => it.track).filter(Boolean);
                if (trackItems.length) {
                    let idx = trackItems.indexOf(track);
                    if (idx < 0) {
                        const key = (t) => `${(t?.artist || '').toLowerCase()}|||${(t?.title || '').toLowerCase()}`;
                        idx = trackItems.findIndex(t => key(t) === key(track));
                    }
                    d(setListContext({ items: trackItems, currentIndex: Math.max(0, idx), type: 'tracks' }));
                }
            }
        });
        const src = track.previewUrl || track.sampleUrl || track.preview_url || track.preview;
        const youtubeId = track.youtubeId && track.youtubeId !== '-1' ? track.youtubeId : null;
        const soundcloudUrl = track.soundcloudUrl || null;
        const base = {
            name: track.title || 'Unknown Track',
            singer: track.artist || '',
            cover: resolveCover(track.artwork, track.artworkUrl, track.image),
            ...(youtubeId ? { youtubeId } : {}),
            ...(soundcloudUrl ? { soundcloudUrl } : {}),
            ...(src ? { samplePreviewUrl: src } : {}),
            ...(track.pageUrl ? { pageUrl: track.pageUrl } : {}),
        };
        // Queue instantly. Full-first policy: a known YT/SC link queues a
        // /lazy-audio URL — the audio server extracts the FULL stream only
        // when the player actually requests it, redirecting to the sample on
        // failure and 404ing (→ skip) when there's no fallback.
        const trackId = entityId(track);
        const canSearch = trackId && track.artist && track.title;
        if ((youtubeId || soundcloudUrl || canSearch) && track.playbackBlocked !== true && !isSamplesOnly) {
            // Known link → extract at play time. No link but identifiable →
            // /lazy-audio runs the same search+score the play button uses,
            // also at play time. Sample rides along as the fallback.
            const q = youtubeId ? `yt=${encodeURIComponent(youtubeId)}`
                : soundcloudUrl ? `sc=${encodeURIComponent(soundcloudUrl)}`
                : `tid=${encodeURIComponent(trackId)}&artist=${encodeURIComponent(track.artist)}&title=${encodeURIComponent(track.title)}`
                    + (track.duration ? `&dur=${encodeURIComponent(track.duration)}` : '')
                    + (() => { try { const t = localStorage.getItem('djpal:token'); return t ? `&tok=${encodeURIComponent(t)}` : ''; } catch { return ''; } })();
            const fb = src ? `&fallback=${encodeURIComponent(src)}` : '';
            addToNextUp(dispatch, {
                ...base,
                musicSrc: `http://localhost:3069/lazy-audio?${q}${fb}`,
                ...(youtubeId || soundcloudUrl ? { originalUrl: youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : soundcloudUrl } : {}),
            });
            // Prefetch the full download NOW (fire-and-forget, deduped): by
            // the time the queue reaches this track, /lazy-audio serves the
            // local file — instant start + waveform ready, same as tracks
            // played directly from the feed (which the feed pre-warms).
            if (youtubeId || soundcloudUrl) {
                const pf = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : soundcloudUrl;
                try { window.electron?.djpal?.getTrackAudio?.(pf, { priority: false })?.catch?.(() => {}); } catch (_) { /* best-effort */ }
            }
            return;
        }
        if (src) {
            addToNextUp(dispatch, { ...base, musicSrc: src });
            return;
        }
        // No sample and no known link: needs a lazy YouTube search first —
        // the one genuinely slow case, so show progress and never fail silent.
        toast.info(`Adding "${track.title || 'track'}" to Next Up…`);
        const resolved = await Promise.race([
            resolveTrackSrc(track),
            new Promise((r) => setTimeout(() => r(null), 45000)),
        ]);
        if (!resolved) {
            toast.warning(`Couldn't load audio for "${track.title || 'track'}"`);
            return;
        }
        addToNextUp(dispatch, {
            name: track.title || 'Unknown Track',
            singer: track.artist || '',
            cover: resolveCover(track.artwork, track.artworkUrl, track.image),
            musicSrc: resolved.musicSrc,
            ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
            ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
            ...(track.pageUrl ? { pageUrl: track.pageUrl } : {}),
        });
    }, [dispatch, toast, items]);

    const handleAddToPlaylist = useCallback((e, track) => {
        e.stopPropagation();
        setPlaylistTrack(track);
    }, []);

    const handleStartSet = useCallback((e, track) => {
        e.stopPropagation();
        const title = (track?.title || '').trim();
        if (!title) return;
        const artist = (track?.artist || '').trim();
        const query = artist ? `${artist} - ${title}` : title;
        dispatch(setPendingStart({
            query,
            meta: {
                title,
                artist,
                artwork: track?.artwork || track?.artworkUrl || track?.image || null,
                previewUrl: track?.previewUrl || track?.sampleUrl || track?.preview_url || track?.preview || null,
            },
        }));
        dispatch(setActiveTab('whats-next'));
    }, [dispatch]);

    // Predict whether the post-mount useEffect will clear-and-refetch this
    // render, so we don't paint the about-to-be-thrown-away cached items
    // before the skeleton appears. Without this, opening a stale For You
    // tab flashes: cached cards → blank → skeleton → fresh cards — three
    // commits' worth of layout work between the click and a useful frame.
    //
    // Mirrors the in-session-stability gate in the useEffect above:
    // skip refetch only when we already have a healthy feed. If we get
    // this wrong (predicts refetch when the effect won't actually fire),
    // the UI sits on the skeleton forever with no network call to
    // recover it — which is exactly the bug we just fixed.
    const willClearAndRefetch = (() => {
        // tokenValid === false: no fetch will ever fire, so never predict one.
        // tokenValid === null (still validating): fall through. The mount
        // effect runs ~200ms later once the token resolves; if it is going
        // to clear, painting the cached list for those 200ms and THEN
        // swapping to skeletons → fresh items is exactly the "feed shows
        // old tracks and replaces them when I click For You" flash.
        if (tokenValid === false) return false;
        if (isLoading) return false;
        // Mirror the actual refetch effect's `haveHealthyFeed` short-circuit:
        // if we already have items, the effect won't refetch even if totals
        // change, so predicting a refetch here would paint the skeleton
        // forever with no network call to recover it.
        const haveHealthyFeed = _hasFetchedOnce && items.length > 0;
        if (haveHealthyFeed) return false;
        // Same known-on-both-sides rule as the mount effect above.
        const fetchedTotalsKnown = lastFetchedLikesTotal != null && lastFetchedFollowsTotal != null;
        const totalsChanged = totalsKnown && fetchedTotalsKnown
            && (likesTotal !== lastFetchedLikesTotal || followsTotal !== lastFetchedFollowsTotal);
        if (_hasFetchedOnce) {
            // Already tried at least once and items are empty. Only predict
            // a refetch if the effect's deps would actually trigger one
            // (stale or totals changed). An empty 0→0 doesn't loop the
            // effect, so predicting "will refetch" pins the skeleton
            // forever for users with no personal data.
            const stale = lastFetchedAt && (Date.now() - lastFetchedAt > 5 * 60 * 1000);
            return Boolean(stale || totalsChanged);
        }
        // First mount, no fetch attempted yet. Mirror the effect exactly: it
        // clears + refetches when the prefetched feed is stale (> 5 min, e.g.
        // the app sat on another tab) or totals changed — so predict the
        // skeleton for those cases instead of flashing the about-to-be-
        // cleared list. A FRESH prefetch (< 5 min) paints immediately.
        if (items.length === 0) return true;
        const stale = !lastFetchedAt || (Date.now() - lastFetchedAt > 5 * 60 * 1000);
        return Boolean(stale || totalsChanged);
    })();
    const visibleItems = willClearAndRefetch ? [] : items;
    const visibleMixes = willClearAndRefetch ? [] : mixes;
    const showSkeleton = (isLoading || tokenValid !== true || willClearAndRefetch) && visibleItems.length === 0;

    // DESIGN V2 — the top 3-4 items become the hero row; the list below starts
    // after them. previewIds stay `feed-${i}` / `mix-${i}` with the ORIGINAL
    // index so the hero and the cards never collide on the same id.
    const v2 = useDesignV2();
    // Mobile v2 (2026-10): the hero row renders on phones too, as a sideways
    // snap strip (styles/design-v2/_mobile.scss).
    const heroCount = (list) => (v2 && list.length >= 3 ? Math.min(4, list.length) : 0);
    const heroTrackItems = (list, origin) => {
        const n = heroCount(list);
        if (!n) return [];
        const trackItems = list.map(it => it.track);
        return list.slice(0, n).map((item, i) => {
            const track = item.track;
            const art = track.artwork || track.artworkUrl || track.image || null;
            const previewId = `${origin === 'trending' ? 'trending' : 'feed'}-${i}`;
            const listen = { trackId: track.id ?? null, src: track.previewUrl || track.sampleUrl || track.preview_url || track.preview || null, previewId, origin };
            const meta = [track.artist, item.mixTitle, item.mixDate ? formatFeedDateRelative(item.mixDate) : null].filter(Boolean).join(' · ');
            return {
                key: previewId,
                art,
                kicker: item.mixDj ? `Played by ${item.mixDj}` : (origin === 'trending' ? 'Trending now' : 'Picked for you'),
                title: track.title || 'Unknown Track',
                meta,
                onOpen: (e) => handleSearchTrack(e, track),
                listen,
                playButton: (
                    <DJPalAudioPreview
                        src={track.previewUrl || track.sampleUrl || track.preview_url || track.preview}
                        youtubeId={track.youtubeId}
                        soundcloudUrl={track.soundcloudUrl}
                        trackId={track.id}
                        durationSec={track.durationSec ?? track.duration}
                        pageUrl={track.pageUrl}
                        previewId={previewId}
                        trackName={track.title}
                        trackArtist={track.artist}
                        trackArtists={track.artists}
                        trackCover={art}
                        playContext={{ items: trackItems, index: i, type: 'tracks', origin }}
                        playbackBlocked={track.playbackBlocked}
                    >
                        <HeroListenPlayButton listen={listen} lead={i === 0} />
                    </DJPalAudioPreview>
                ),
            };
        });
    };
    const heroMixItems = (list) => {
        const n = heroCount(list);
        if (!n) return [];
        const feedMixes = list.map(m => ({ ...m, djName: m.djName || m.dj || '' }));
        return list.slice(0, n).map((mix, i) => {
            const mixId = mixTrackingKey(mix);
            const dj = mix.djName || mix.dj || '';
            const trackCount = mix.trackCount ?? mix.tracksCount ?? (Array.isArray(mix.tracks) ? mix.tracks.length : null);
            const meta = [
                formatHeroDuration(mix.durationSec ?? mix.duration),
                trackCount ? `${trackCount} tracks` : null,
                mix.date ? formatFeedDateRelative(mix.date) : null,
            ].filter(Boolean).join(' · ');
            return {
                key: mixId || `mix-${i}`,
                art: resolveMixCover(mix),
                kicker: dj ? `New from ${dj}` : 'New mix',
                title: mix.title || 'Untitled Mix',
                meta,
                onOpen: () => handleOpenMix(mix),
                onPlay: isNative ? null : () => handlePlayMix(mix, { items: feedMixes, index: i }),
                playing: !!mixId && playingMixId === mixId,
                loading: !!mixId && loadingMixId === mixId,
                secondary: <button type="button" className="v2-btn" onClick={() => handleOpenMix(mix)}>Tracklist</button>,
            };
        });
    };

    // Only surface the error screen once the retry loop has given up. While
    // retries are still in flight, fall through to the normal skeleton so the
    // UI doesn't flash between "connecting" and the error prompt on each
    // attempt (connectDjpal toggles tokenError null→set every retry). The
    // feed has no token-free shell (everything is server content), so a
    // full-screen prompt — not a banner — is right here.
    if (connectFailed) {
        return (
            <div className="tab-container feed-tab">
                <div className="feed-connect-prompt">
                    <div className="prompt-title">For You</div>
                    <div className="prompt-description">Connection failed: {tokenError}</div>
                    <button className="prompt-btn" onClick={retryConnect}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="tab-container feed-tab">
            <div className={`lower-part${v2 ? ' v2-ambient' : ''}`} ref={scrollRef}>
                <div className="feed-header" ref={headerRef}>
                    <div className="feed-header-left">
                        <h3 className="feed-title">For You</h3>
                        <div className="feed-subtabs" role="tablist">
                            <button
                                role="tab"
                                aria-selected={subTab === 'tracks'}
                                className={`feed-subtab${subTab === 'tracks' ? ' active' : ''}`}
                                onClick={() => setSubTab('tracks')}
                            >
                                Tracks
                            </button>
                            <button
                                role="tab"
                                aria-selected={subTab === 'mixes'}
                                className={`feed-subtab${subTab === 'mixes' ? ' active' : ''}`}
                                onClick={() => setSubTab('mixes')}
                            >
                                Mixes
                            </button>
                            <button
                                role="tab"
                                aria-selected={subTab === 'trending'}
                                className={`feed-subtab${subTab === 'trending' ? ' active' : ''}`}
                                onClick={() => setSubTab('trending')}
                            >
                                Trending
                            </button>
                        </div>
                    </div>
                    <div className="feed-header-right">
                        <div className="feed-header-actions">
                            {/* DESIGN V2: the gift icon stays; while a surprise feed is
                                showing the label says what the next click does ("New
                                surprise" re-rolls) and a quiet link goes back. */}
                            {v2 && subTab !== 'trending' && surpriseActive && (
                                <button type="button" className="feed-surprise-back" onClick={handleSurprise} disabled={isLoading}>Back to your feed</button>
                            )}
                            {subTab !== 'trending' && (
                                <button className={`feed-surprise-btn${surpriseActive ? ' active' : ''}`} onClick={v2 && surpriseActive ? handleRefresh : handleSurprise} disabled={isLoading} title={surpriseActive ? (v2 ? 'Showing a surprise feed — click for another' : 'Showing a Surprise feed — click to return to your feed (Refresh re-rolls)') : 'Surprise me — a random feed in this era, ignoring your likes'}>
                                    <SurpriseIcon width="14" height="14" />
                                    <span className="feed-btn-label">{v2 ? (surpriseActive ? 'New surprise' : 'Surprise me') : 'Surprise Me'}</span>
                                </button>
                            )}
                            {/* While a surprise feed shows, Refresh and Surprise would do the
                                same thing (re-roll) — one control, so Refresh steps aside. */}
                            {!(v2 && surpriseActive && subTab !== 'trending') && (
                            <button className="feed-refresh-btn" onClick={handleRefresh} disabled={isLoading} title="Refresh">
                                <svg className={`feed-refresh-icon${isLoading ? ' spinning' : ''}`} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10"/>
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                </svg>
                            </button>
                            )}
                        </div>
                        {subTab !== 'trending' && (
                            <div className="feed-time-control">
                                <span className="feed-time-label">Throwback</span>
                                <div className="feed-time-slider-wrap">
                                    <input
                                        type="range"
                                        className="feed-time-slider"
                                        min="-1"
                                        max="1"
                                        step="0.05"
                                        value={sliderValue}
                                        onChange={handleSliderChange}
                                        onPointerUp={handleSliderRelease}
                                        disabled={isLoading}
                                    />
                                    {v2 && (
                                        <span className="feed-time-value" style={{ left: `calc(7px + (100% - 14px) * ${(sliderValue + 1) / 2})` }} aria-hidden="true">{feedEraLabel(sliderValue)}</span>
                                    )}
                                </div>
                                <span className="feed-time-label">Fresh</span>
                            </div>
                        )}
                    </div>
                </div>
                {tasteNotice && (
                    <div className="feed-taste-notice" role="status">{tasteNotice}</div>
                )}

                {goatCut && !showSkeleton && (
                    <div className="feed-mode-banner feed-goat-cut-banner" role="alert">
                        <span className="feed-mode-banner-icon" aria-hidden="true">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 8v4" />
                                <path d="M12 16h.01" />
                            </svg>
                        </span>
                        <span className="feed-mode-banner-text">
                            We couldn't confirm your GOAT plan with our server, so you're seeing the short list.
                        </span>
                        <button type="button" className="btn-secondary feed-goat-cut-retry" onClick={handleGoatCutRetry}>
                            Try again
                        </button>
                    </div>
                )}

                {subTab === 'trending' && hasNoPersonalData && (
                    <div className="feed-mode-banner">
                        <span className="feed-mode-banner-text">
                            Showing what DJs are spinning right now. To get a personalized For You feed, sync your library from the sidebar or like tracks and follow artists.
                        </span>
                    </div>
                )}

                {subTab !== 'trending' && !personalizeNudgeDismissed && !surpriseActive && discovery >= 0.5 && signalCountKnown && knownSignalCount < 25 && !showSkeleton && (items.length > 0 || mixes.length > 0) && (
                    <div className="feed-mode-banner">
                        <span className="feed-mode-banner-icon" aria-hidden="true">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                                <path d="M20 3v4" />
                                <path d="M22 5h-4" />
                                <path d="M4 17v2" />
                                <path d="M5 18H3" />
                            </svg>
                        </span>
                        <span className="feed-mode-banner-text">
                            Like tracks, follow artists, or sync your library to make this feed yours.
                        </span>
                        <button className="feed-banner-dismiss" onClick={dismissPersonalizeNudge} aria-label="Dismiss" title="Dismiss">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                )}

                {subTab !== 'trending' && showSkeleton && (
                    <div className="feed-skeletons">
                        {[0, 1, 2].map(i => (
                            <div key={i} className="feed-skeleton-card">
                                <div className="feed-skeleton-art" />
                                <div className="feed-skeleton-content">
                                    <div className="feed-skeleton-line short" />
                                    <div className="feed-skeleton-line" />
                                    <div className="feed-skeleton-wave" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {error && subTab !== 'trending' && (
                    // A FAILED fetch, not an empty one. This used to reuse the
                    // "Nothing here yet / sync your library" copy, which told a
                    // user with hundreds of likes that they had no taste data
                    // whenever the radio wasn't up yet on app resume (Android,
                    // 2026-08-19). Say what happened and offer a way out.
                    <div className="feed-empty-state feed-error-state">
                        <div className="feed-empty-icon" style={{ fontSize: 48 }}>&#9835;</div>
                        <p className="feed-empty-title">Couldn't load your feed</p>
                        <p className="feed-empty-desc">{error}</p>
                        <button type="button" className="prompt-btn feed-retry-btn" onClick={handleRefresh}>Retry</button>
                    </div>
                )}

                {!isLoading && !error && subTab === 'tracks' && visibleItems.length === 0 && lastFetchedAt && !willClearAndRefetch && (
                    <div className="feed-empty-state">
                        <div className="feed-empty-icon" style={{ fontSize: 48 }}>&#9835;</div>
                        <p className="feed-empty-title">
                            {hasNoPersonalData ? "Let's build your For You feed" : 'Nothing turned up'}
                        </p>
                        <p className="feed-empty-desc">
                            {hasNoPersonalData
                                ? "Sync your library from the sidebar, like some tracks, or follow artists — then refresh to get personalized recommendations. Or check the Trending tab for what DJs are spinning now."
                                : 'Try refreshing or like a few tracks to give us more to work with.'}
                        </p>
                    </div>
                )}

                {!isLoading && !error && subTab === 'mixes' && (!visibleMixes || visibleMixes.length === 0) && lastFetchedAt && !willClearAndRefetch && (
                    <div className="feed-empty-state">
                        <div className="feed-empty-icon" style={{ fontSize: 48 }}>&#9835;</div>
                        <p className="feed-empty-title">
                            {hasNoPersonalData ? "Let's build your For You feed" : 'No mixes yet'}
                        </p>
                        <p className="feed-empty-desc">
                            {hasNoPersonalData
                                ? "Sync your library or follow artists — we'll start surfacing mixes that match your taste. Or check the Trending tab for what DJs are spinning now."
                                : "Like more tracks or follow artists — we'll surface mixes that match your taste."}
                        </p>
                    </div>
                )}

                {subTab === 'tracks' && visibleItems.length > 0 && heroCount(visibleItems) > 0 && (
                    <>
                        <DiscoverHero items={heroTrackItems(visibleItems, 'feed')} origin="feed" />
                        {visibleItems.length > heroCount(visibleItems) && <SectionHeader title="More for you" />}
                    </>
                )}
                {subTab === 'tracks' && visibleItems.length > 0 && (
                    <div className="feed-cards">
                        {(() => {
                            const trackItems = visibleItems.map(it => it.track);
                            const from = heroCount(visibleItems);
                            return visibleItems.slice(from, Math.max(visibleCount, from)).map((item, j) => (i => (
                                <FeedCard
                                    key={`feed-${i}`}
                                    item={item}
                                    index={i}
                                    timeMode={timeMode}
                                    onCopy={handleCopyTrack}
                                    onSearchTrack={handleSearchTrack}
                                    onSearchArtist={handleSearchArtist}
                                    onSearchMix={handleSearchMix}
                                    onAddToQueue={handleAddToQueue}
                                    onAddToPlaylist={handleAddToPlaylist}
                                    onStartSet={handleStartSet}
                                    copiedId={copiedTrackId}
                                    playContext={{ items: trackItems, index: i, type: 'tracks', origin: 'feed' }}
                                    surpriseMode={surpriseActive}
                                />
                            ))(j + from));
                        })()}
                        {freeLimit?.moreCount > 0 && (
                            <FeedFreeWall list="tracks" shown={freeLimit.shown} moreCount={freeLimit.moreCount} onUnlock={() => requireUpgrade({ list: 'tracks', moreCount: freeLimit.moreCount })} />
                        )}
                        {isLoadingMore && (
                            <div className="feed-loading-more">
                                <div className="feed-spinner" />
                            </div>
                        )}
                    </div>
                )}

                {subTab === 'trending' && trendingItems.length > 0 && heroCount(trendingItems) > 0 && (
                    <>
                        <DiscoverHero items={heroTrackItems(trendingItems, 'trending')} origin="trending" />
                        {trendingItems.length > heroCount(trendingItems) && <SectionHeader title="Trending now" />}
                    </>
                )}
                {subTab === 'trending' && trendingItems.length > 0 && (
                    <div className="feed-cards">
                        {(() => {
                            const trackItems = trendingItems.map(it => it.track);
                            const from = heroCount(trendingItems);
                            return trendingItems.slice(from, Math.max(visibleCount, from)).map((item, j) => (i => (
                                <FeedCard
                                    key={`trending-${i}`}
                                    item={item}
                                    index={i}
                                    timeMode={timeMode}
                                    onCopy={handleCopyTrack}
                                    onSearchTrack={handleSearchTrack}
                                    onSearchArtist={handleSearchArtist}
                                    onSearchMix={handleSearchMix}
                                    onAddToQueue={handleAddToQueue}
                                    onAddToPlaylist={handleAddToPlaylist}
                                    onStartSet={handleStartSet}
                                    copiedId={copiedTrackId}
                                    playContext={{ items: trackItems, index: i, type: 'tracks', origin: 'trending' }}
                                />
                            ))(j + from));
                        })()}
                        {trendingFreeLimit?.moreCount > 0 && (
                            <FeedFreeWall list="trending" shown={trendingFreeLimit.shown} moreCount={trendingFreeLimit.moreCount} onUnlock={() => requireUpgrade({ list: 'trending', moreCount: trendingFreeLimit.moreCount })} />
                        )}
                    </div>
                )}

                {subTab === 'trending' && trendingIsLoading && trendingItems.length === 0 && (
                    <div className="feed-skeletons">
                        {[0, 1, 2].map(i => (
                            <div key={i} className="feed-skeleton-card">
                                <div className="feed-skeleton-art" />
                                <div className="feed-skeleton-content">
                                    <div className="feed-skeleton-line short" />
                                    <div className="feed-skeleton-line" />
                                    <div className="feed-skeleton-wave" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {subTab === 'trending' && trendingError && (
                    <div className="feed-empty-state feed-error-state">
                        <div className="feed-empty-icon" style={{ fontSize: 48 }}>&#9835;</div>
                        <p className="feed-empty-title">Couldn't load trending</p>
                        <p className="feed-empty-desc">{trendingError}</p>
                        <button type="button" className="prompt-btn feed-retry-btn" onClick={handleRefresh}>Retry</button>
                    </div>
                )}

                {subTab === 'mixes' && visibleMixes && visibleMixes.length > 0 && heroCount(visibleMixes) > 0 && (
                    <>
                        <DiscoverHero items={heroMixItems(visibleMixes)} origin="mixes" />
                        {visibleMixes.length > heroCount(visibleMixes) && <SectionHeader title="More mixes for you" />}
                    </>
                )}
                {subTab === 'mixes' && visibleMixes && visibleMixes.length > 0 && (
                    <div className="feed-cards">
                        {(() => {
                            const feedMixes = visibleMixes.map(m => ({ ...m, djName: m.djName || m.dj || '' }));
                            const from = heroCount(visibleMixes);
                            return visibleMixes.slice(from, Math.max(visibleCount, from)).map((mix, j) => {
                                const i = j + from;
                                const mixId = mixTrackingKey(mix);
                                return (
                                    <MixFeedCard
                                        key={mixId || `mix-${i}`}
                                        mix={mix}
                                        index={i}
                                        onOpen={handleOpenMix}
                                        onCopy={handleCopyMix}
                                        copiedId={copiedTrackId}
                                        onPickTrack={(track) => handleSearchTrack({ stopPropagation: () => {} }, track)}
                                        onPickArtist={(artist) => {
                                            const name = typeof artist === 'string' ? artist : (artist?.name || '');
                                            const id = typeof artist === 'object' ? (artist?.id || null) : null;
                                            const art = typeof artist === 'object' ? (artist?.artworkUrl || null) : null;
                                            handleSearchArtist({ stopPropagation: () => {} }, name, id, art);
                                        }}
                                        onPlay={handlePlayMix}
                                        onQueue={handleQueueMix}
                                        onAddToPlaylist={handleAddToPlaylist}
                                        isLoading={loadingMixId === mixId}
                                        isPlaying={playingMixId === mixId}
                                        playContext={{ items: feedMixes, index: i }}
                                    />
                                );
                            });
                        })()}
                        {freeLimit?.mixes?.moreCount > 0 && (
                            <FeedFreeWall list="mixes" shown={freeLimit.mixes.shown} moreCount={freeLimit.mixes.moreCount} onUnlock={() => requireUpgrade({ list: 'mixes', moreCount: freeLimit.mixes.moreCount })} />
                        )}
                    </div>
                )}
            </div>

            {playlistTrack && ReactDOM.createPortal(
                <AddToPlaylistModal
                    show={!!playlistTrack}
                    onHide={() => setPlaylistTrack(null)}
                    track={playlistTrack}
                />,
                document.body
            )}
        </div>
    );
};

// The mix card is reused by the Mixpedia "See all" list view.
export { MixFeedCard };
export default FeedTab;
